import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useGenerationState,
  GENERATION_MODEL_SPEED,
  DEFAULT_SCENARIO_MODEL,
  SCENARIO_MODEL_RANDOM,
  formatVideoResolutionLabel,
  type VideoGenerationMode,
} from '../state/useGenerationState';
import { useQueryClient } from '@tanstack/react-query';
import { scenarioModelTierLabel } from './scenarioModelCatalog';
import styles from './ScenarioGenerator.module.css';
import '../../../styles/form-controls.css';
import '../../../styles/generation-common.css';
import '../../../styles/buttons.css';
import '../../../styles/badges.css';
import { Z_INDEX } from '../../../styles/zIndex';
import { useToast } from '../../../hooks/useToast';
import { useTemporaryMaterials, useCloudMaterials } from '../../../features/materials/hooks';
import { useCreditRates, useStorageInfo } from '../../../features/organizations/hooks';
import { useCredits } from '../../../hooks/useCredits';
import { usePurchaseModal } from '../../../hooks/usePurchaseModal';
import { ProductSelectorModal } from '../../materials/shared/ProductSelectorModal';
import { type VideoSettings, useEstimatedVideoCredits } from './VideoGenerationSettings';
import { GenerateButton } from '../common/GenerateButton';
import { MaterialPreviewModal } from '../../materials/shared/MaterialPreviewModal';
import { fetchMaterialById, uploadTemporaryMaterial, fetchMaterialSignedUrl } from '../../../features/materials/api';
import { resolveMaterialUrls, buildDragData } from '../../../features/materials/materialUrl';
import type { GenerationAsset, GenerationAssetUsage } from '../state/useGenerationState';
import { useScenarioPromptOptions, useScenarioBrandLogoStatus, useUpdateScenario, useScenarioProductBrandInfo, useLinkProductToBrand, useLinkScenarioBrand, useDuplicateScenario } from '../../../features/scenarios/hooks';
import { ScenarioBookModal } from '../../../features/scenarios/components';
import { LoadingOverlay } from '../../shared/LoadingOverlay';
import { useUser } from '../../../features/auth/useUser';
import { AddSceneModal } from './AddSceneModal';
import { type VideoJobRestorePayload } from './VideoJobHistoryPanel';
import { updateScenario } from '../../../features/scenarios/api';
import type { UpdateScenarioPayload } from '../../../features/scenarios/types';
import { deriveLoadedVideoGenerationMode } from '../../../features/scenarios/productionMode';
import { toScenarioVideoGenerationMode } from '../../../features/scenarios/videoGenerationMode';
import { MOTION_LAYERED_AUTO_GENERATE_NOTICE, resolvePromptAutoGenerateAction } from '../../../features/motion/layeredPromptMode';
import { useMotionGraphicsTabVisible } from '../../../features/motion/useMotionGraphicsTabVisible';
import { downloadSelectedAssetsTile, renderSelectedAssetsTileBlob } from './downloadAssetsTile';
import { type HierarchicalOption, type RecommendedKeys } from '../../common/HierarchicalSelect';
import { findShootingMethodLabel } from '../state/shootingMethod';
import { MOTION_GRAPHICS_WHOLE, effectiveMotionGraphics } from '../state/motionGraphics';
import { formatTargetAudienceSummary } from '../state/targetAudience';
import { useRecommendations, resolveRecommendationSubject } from '../hooks/useRecommendations';
import { useRecommendationEvents } from '../hooks/useRecommendationEvents';
import { useGenerationModels } from '../../../features/generation-models/hooks';
import { buildMentionActorSources, buildMentionReferenceLimitContext, filterActorSourcesWithinReferenceLimit, filterAssetsWithinReferenceLimit, filterMentionActorSources } from '../state/mentionUtils';
import { isBelowMinReferenceSize, isGeneratedSceneFrameAssetId } from '../state/assetMediaUtils';
import { useVisualStyleStylizedMap } from '../state/scenarioAttributesContext';
import { resolveVisualStyleStylized } from '../state/visualStyle';
import { isCharacterActor } from '../../../features/actors/actorAttributes';
import { SEEDANCE_MIN_REFERENCE_IMAGE_SHORT_SIDE, STORAGE_KEY_DURATION } from './constants';
import { usePromptModels } from '../hooks/usePromptModels';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { useSceneActions } from './hooks/useSceneActions';
import { useMotionSyncStatus } from './hooks/useMotionSyncStatus';
import { useImportReportAutoRefresh } from './hooks/useImportReportAutoRefresh';
import { useVoiceLineHandlers } from './hooks/useVoiceLineHandlers';
import { deriveMotionVoiceToggle, voiceToBehaviorValue } from './motionVoiceToggle';
import { usePublishConfirm } from '../../../features/scenarios/usePublishConfirm';
import { useScenarioGeneration } from './hooks/useScenarioGeneration';
import { useScenarioExport } from './hooks/useScenarioExport';
import { useNarrationHandlers } from './hooks/useNarrationHandlers';
import { useAutoStoryboardGeneration } from './hooks/useStoryboardGeneration';
import { useVideoHandlers } from './hooks/useVideoHandlers';
import { useCastingHydration } from './hooks/useCastingHydration';
import { useFrameHandlers } from './hooks/useFrameHandlers';
import { SceneStateProvider, useSceneStateContext } from './hooks/SceneStateContext';
import { SceneHandlersProvider, type SceneHandlersContextValue } from './hooks/SceneHandlersContext';
import { useLocalStorageSync } from './hooks/useLocalStorageSync';
import { useScenarioFormState } from './hooks/useScenarioFormState';
import { useScenarioConfirmDialogs } from './hooks/useScenarioConfirmDialogs';
import { ScenarioSetupProvider, type ScenarioSetupContextValue } from './hooks/ScenarioSetupContext';
import type { ImportedScriptState } from './scriptImport';
import {
  SOURCE_VIDEO_DEFAULT_DURATION_SEC,
  durationSecondsOf,
  initialSourceVideoState,
  readSourceVideoState,
  sourceVideoSummaryLabel,
  syncSourceVideoDuration,
  type SourceVideoState,
} from './sourceVideo';
import { useGenerationSettings } from '../state/settingsContext';
import { useCreditCalculations } from './hooks/useCreditCalculations';
import { useScenarioModelOptions } from './hooks/useScenarioModelOptions';
import { useProductMaterials } from './hooks/useProductMaterials';
import { usePortalDropdown } from './hooks/usePortalDropdown';
import { useProductSelection } from './hooks/useProductSelection';
import { useBgmEditor } from './hooks/useBgmEditor';
import { isLyricsBgmTone, isDurationBelowLyricsMin, findMinLyricsDurationOption } from './lyricsDurationGate';
import { resolveDurationOption } from './customDuration';
import {
  MOTION_SCENARIO_COUNT, MOTION_SCENARIO_COUNT_FORCED_MESSAGE,
  filterLyricsTonesForMotion, isDurationOverMotionWholeMax, resolveMotionEngine,
} from './motionScenario';
import { MotionBgmChoiceModal } from './MotionBgmChoiceModal';
import type { MotionBgmChoice } from '../../../features/scenarios/api';
import { useScenarioInfoModal } from './hooks/useScenarioInfoModal';
import { useSetupAccordions } from './hooks/useSetupAccordions';
import { useGlobalDragTracking } from './hooks/useGlobalDragTracking';
import { useStickySubmitVisibility } from './hooks/useStickySubmitVisibility';
import { useScenarioTabsDrag } from './hooks/useScenarioTabsDrag';
import { useFirstFrameSync } from './hooks/useFirstFrameSync';
import { SetupPanel } from './SetupPanel';
import { WorkPanel } from './WorkPanel';
import { ScenarioDialogs } from './ScenarioDialogs';
import { FrameModelDropdownPortal } from './FrameModelDropdownPortal';
import { VideoModelDropdownPortal } from './VideoModelDropdownPortal';
import type { DroppedMaterial, Scenario, ScenarioResponse, Mode } from './types';
import { probeVideoDurationSeconds, planCastingParamSelection, resolveDeepLinkCastPendingTarget, shouldShowCastingSheetToggle } from './utils';
import { resolveModelParamQueryOutcome } from '../utils/modelParamResolution';
import { resolveErrorMessage, getResponseErrorMessage } from '../../../utils/getErrorMessage';
import { brandConfig } from '../../../config/brands';
import { useSceneFrameSettings } from './hooks/useSceneFrameSettings';

interface ScenarioGeneratorProps {
  initialMode?: 'attributes' | 'prompt';
  onModeChange?: (mode: 'attributes' | 'prompt') => void;
}

export function ScenarioGenerator(props: ScenarioGeneratorProps) {
  return (
    <SceneStateProvider>
      <ScenarioGeneratorInner {...props} />
    </SceneStateProvider>
  );
}

function ScenarioGeneratorInner({ initialMode, onModeChange }: ScenarioGeneratorProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { scenarioId: scenarioIdFromPath } = useParams<{ scenarioId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const tempMaterials = useTemporaryMaterials();
  const cloudMaterials = useCloudMaterials();
  const { selectedAssets, maxReferenceVideos, setProductionMode, requestOpenPersonalFolder, addAsset, removeAsset, clearAssets, replaceAssets, videoGenerationMode: scenarioVideoGenMode, setVideoGenerationMode: setScenarioVideoGenMode, setVideoGenerationModeTransient: setScenarioVideoGenModeTransient, restoreVideoGenerationMode, promptVideoGenerationMode: promptVideoGenMode, setPromptVideoGenerationMode: setPromptVideoGenMode, scenarioInputTab, setScenarioInputTab, pendingPrompt, pendingMode, clearPendingEdit, pendingVideoEditEntry, clearVideoEditEntry, settings, selectedCasting, setSelectedCasting, additionalCastings, setAdditionalCastings, castingSelectionState, setCastingSelectionState, markCastingExplicit, pendingDeepLinkCastScenarioId, setPendingDeepLinkCastScenarioId, consumePendingDeepLinkCast, selectedSeedancePresetActor, setSelectedSeedancePresetActor, additionalSeedancePresetActors, setAdditionalSeedancePresetActors, castingPosition, activeScenarioId, setActiveScenarioId, activeJobs, videoSettings, setVideoSettings, avoidFaces, setAvoidFaces, illustrationStoryboard, setIllustrationStoryboard, visualStyle, setVisualStyle, shootingMethod, shootingMethodOptions, motionGraphics, motionGraphicsOptions, dialect, dialectOptions, dialectVoiceAccents, targetGender, targetGenderOptions, targetAge, targetAgeOptions, visualStyleHierarchicalOptions, visualStyleTreeEnabled, visualStyleIsStylized, visualStyleFieldPlacement, toneTreeOptions, toneTreeEnabled, characterCountTreeOptions, characterCountTreeEnabled, narrationTreeOptions, narrationTreeEnabled, narrationCategoryEnabled, dialogueTreeOptions, dialogueTreeEnabled, dialogueCategoryEnabled, telopTreeOptions, telopTreeEnabled, telopCategoryEnabled, scenarioAttributesLoaded, includeStoryboardLogo, setIncludeStoryboardLogo, includeStoryboardProductImage, setIncludeStoryboardProductImage, facePrivacySanitizedIds, setFacePrivacyFlagged, } = useGenerationState();
  // 映像スタイル変更時の矛盾キャスト自動解除 (P5-3) 用。visualStyleIsStylized は現在の
  // visualStyle に対する値であり、変更ハンドラ内では「変更後」の stylized 判定を
  // 別途その場で計算する必要があるためマップを直接持つ。
  const visualStyleStylizedMap = useVisualStyleStylizedMap();
  const { data: creditRatesData } = useCreditRates();
  const { data: creditsData, refetch: refetchCredits } = useCredits();
  const { openPurchaseModal, purchaseModals } = usePurchaseModal();
  const { data: storageData } = useStorageInfo();
  const { user } = useUser();
  const { data: promptOptions } = useScenarioPromptOptions();
  const openMaterialsPanel = useMaterialsPanelStore((state) => state.open);
  const setForcedAspectRatios = useMaterialsPanelStore((state) => state.setForcedAspectRatios);
  const setForcedFileTypes = useMaterialsPanelStore((state) => state.setForcedFileTypes);

  const {
    scenePreviewIndex,
    scenePreviewMaterials,
    scenePreviewLoading,
    lastFrameLoading,
    sceneVideoLoading,
    setSceneVideoGenerated,
    setSceneVideoMaterialId,
    sceneLastFrames, setSceneLastFrames,
    playedVideos,
    playedNarrations,
  } = useSceneStateContext();

  // トーン選択肢を HierarchicalOption[] に変換
  // 'tone' が新ツリー経路に切替済み(toneTreeEnabled)なら属性ツリー由来を優先する(フェーズ3)。
  const toneHierarchicalOptions = useMemo<HierarchicalOption[]>(() => {
    if (toneTreeEnabled) return toneTreeOptions;
    const toneData = promptOptions?.tone;
    if (!toneData) return [];

    // グループがある場合は階層構造を使用
    if (toneData.groups && toneData.groups.length > 0) {
      return toneData.groups.map((group) => {
        if ('children' in group && group.children) {
          return {
            id: group.id,
            key: group.id,
            label: group.label,
            type: 'group',
            children: group.children.map((opt) => ({
              id: opt.id,
              key: opt.value,
              label: opt.label,
              type: 'option',
            })),
          };
        }
        // グループに属さないオプション
        return {
          id: group.id,
          key: (group as unknown as { value: string }).value ?? group.id,
          label: group.label,
          type: 'option',
        };
      });
    }

    // フラット構造のフォールバック
    return toneData.options.map((opt) => ({
      id: opt.id,
      key: opt.value,
      label: opt.label,
      type: 'option',
    }));
  }, [toneTreeEnabled, toneTreeOptions, promptOptions?.tone]);

  // 商品AIプロファイル推奨 (計画書§5.4/§5.5/§8 P4)。選択中素材の先頭1件から subject を
  // 導出し、トーン/映像スタイル/想定尺の推奨を取得する。既定選択はしない
  // (P2方針の踏襲): 上位ソート＋バッジのみ。
  const recommendationSubject = useMemo(
    () => resolveRecommendationSubject(selectedAssets),
    [selectedAssets],
  );
  const recommendationsQuery = useRecommendations(recommendationSubject);
  const recommendations = recommendationsQuery.data;
  const toneRecommendedKeys = useMemo<RecommendedKeys | undefined>(() => {
    if (recommendations?.status !== 'ready' || !recommendations.tone) return undefined;
    return { strong: [], suggested: [recommendations.tone.value] };
  }, [recommendations]);
  const visualStyleRecommendedKeys = useMemo<RecommendedKeys | undefined>(() => {
    if (recommendations?.status !== 'ready' || !recommendations.visual_style) return undefined;
    return { strong: [], suggested: [recommendations.visual_style.value] };
  }, [recommendations]);
  const motionGraphicsRecommendedKeys = useMemo<RecommendedKeys | undefined>(() => {
    if (recommendations?.status !== 'ready' || !recommendations.motion_graphics) return undefined;
    return { strong: [], suggested: [recommendations.motion_graphics.value] };
  }, [recommendations]);
  const recommendedDuration = recommendations?.status === 'ready' ? (recommendations.duration ?? null) : null;

  // Initialize mode - default to 'asin', or 'prompt' if initialMode is 'prompt'
  const [mode, setMode] = useState<Mode>(initialMode === 'prompt' ? 'prompt' : 'asin');

  const videoGenerationMode = mode === 'prompt' ? promptVideoGenMode : scenarioVideoGenMode;
  const setVideoGenerationMode = mode === 'prompt' ? setPromptVideoGenMode : setScenarioVideoGenMode;

  // production (シナリオ/プロンプト) をストアへ同期。store 内 (addAsset 等) で有効な
  // 動画生成モードを判定するために必要。'asin' はシナリオ扱い。
  useEffect(() => {
    setProductionMode(mode === 'prompt' ? 'prompt' : 'scenario');
  }, [mode, setProductionMode]);
  // 表示同期用の非永続セッター (シナリオ mode のみ。prompt mode は従来通り永続セッター)
  const setVideoGenerationModeTransient = mode === 'prompt' ? setPromptVideoGenMode : setScenarioVideoGenModeTransient;

  // Scenario Book Modal state
  const [isScenarioBookOpen, setIsScenarioBookOpen] = useState(false);

  // Open scenario book from URL parameter (e.g. ?scenarioBook=1)
  useEffect(() => {
    if (searchParams.get('scenarioBook')) {
      setIsScenarioBookOpen(true);
    }
  }, [searchParams]);

  // Add scene modal state
  const [addSceneModalScenarioId, setAddSceneModalScenarioId] = useState<string | null>(null);
  const [addSceneGeneratingLabel, setAddSceneGeneratingLabel] = useState('');
  // モーダル内でブランドを新規作成した直後かどうか。タイトル/エンドカードを生成すると
  // 「この商品をブランドに関連付けますか？」プロンプトをオーバーレイに出すため、その間は
  // モーダルを即閉じせず開いたままにする (それ以外は即閉じてスケルトンを見せる)。
  const [brandJustCreatedInModal, setBrandJustCreatedInModal] = useState(false);
  const brandLogoStatus = useScenarioBrandLogoStatus(addSceneModalScenarioId);
  const productBrandInfo = useScenarioProductBrandInfo(addSceneModalScenarioId);
  // モーダルを閉じたら「ブランド新規作成直後」フラグをリセット (再オープン時に持ち越さない)
  useEffect(() => {
    if (!addSceneModalScenarioId) setBrandJustCreatedInModal(false);
  }, [addSceneModalScenarioId]);
  // 素材タイル書き出しのタイトル用: アクティブシナリオに紐づく brands テーブルのブランド名
  const activeScenarioBrand = useScenarioBrandLogoStatus(activeScenarioId);
  const linkProductMutation = useLinkProductToBrand();
  const linkScenarioBrandMutation = useLinkScenarioBrand();
  const [linkProductStatus, setLinkProductStatus] = useState<'idle' | 'pending' | 'success'>('idle');

  // Ref for prompt textarea to focus when switching to prompt mode
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);

  // フレーム生成モデル選択カスタムドロップダウン（各ドロップダウンを独立管理）
  const frameDropdown = usePortalDropdown({
    triggerClassName: styles.frameModelDropdownTrigger,
    groupClassName: styles.frameGenerateGroup,
  });
  const openFrameDropdown = frameDropdown.open;

  // 動画モデル選択カスタムドロップダウン（シーン単位）
  const videoModelDropdown = usePortalDropdown({
    triggerClassName: styles.videoModelDropdownTrigger,
    groupClassName: styles.videoGenerateGroup,
    align: 'right',
  });
  const openVideoModelDropdown = videoModelDropdown.open;
  // idからsceneKeyを抽出 (format: "video-{sceneKey}")
  const videoModelDropdownSceneKey = videoModelDropdown.openId?.replace(/^video-/, '') ?? null;

  // 商品選択カードのref（バリデーション時のスクロール用）
  const productSelectRef = useRef<HTMLButtonElement>(null);
  const [showProductTooltip, setShowProductTooltip] = useState(false);

  useEffect(() => {
    if (!showProductTooltip) return;
    const hide = () => setShowProductTooltip(false);
    const frame = requestAnimationFrame(() => {
      document.addEventListener('click', hide, { once: true });
    });
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('click', hide);
    };
  }, [showProductTooltip]);

  // Stickyボタン用

  // Initialize scenarioInputTab to 'product' on mount (only if not coming from video generation)
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current && pendingMode !== 'video' && videoGenerationMode !== 'frame') {
      setScenarioInputTab('product');
      hasInitializedRef.current = true;
    }
  }, [setScenarioInputTab, pendingMode, videoGenerationMode]);

  // Reset prompt video state when leaving prompt mode
  useEffect(() => {
    if (mode !== 'prompt') {
      setPromptVideoGenerated(false);
      setPromptVideoMaterialId(null);
    }
  }, [mode]);

  // プロンプト入力モード + フレームモード時に素材パネルのアスペクト比 / 種別フィルタを強制
  useEffect(() => {
    if (mode === 'prompt' && videoGenerationMode === 'frame') {
      setForcedAspectRatios(['16:9', '9:16']);
      setForcedFileTypes(['image']);
    } else {
      setForcedAspectRatios(null);
      setForcedFileTypes(null);
    }
    return () => {
      setForcedAspectRatios(null);
      setForcedFileTypes(null);
    };
  }, [mode, videoGenerationMode, setForcedAspectRatios, setForcedFileTypes]);

  // Handle pending video generation mode and URL parameters
  useEffect(() => {
    const urlMode = searchParams.get('mode') as Mode | null;


    if (pendingMode === 'video') {
      setMode('prompt');
      if (pendingPrompt) {
        setVideoPrompt(pendingPrompt);
      }
      clearPendingEdit();
      // Focus on prompt textarea after a short delay
      setTimeout(() => {
        promptTextareaRef.current?.focus();
      }, 100);
    } else if (urlMode === 'prompt' || urlMode === 'asin' || urlMode === 'custom') {
      setMode(urlMode);
      // Focus on prompt textarea if in prompt mode
      if (urlMode === 'prompt') {
        setTimeout(() => {
          promptTextareaRef.current?.focus();
        }, 100);
      }
    }
    // setVideoPrompt は form (useScenarioFormState) 由来の安定 setter だが、この effect は
    // フック呼び出しより前に定義されているため依存配列には入れない (TDZ 回避)。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMode, pendingPrompt, clearPendingEdit, searchParams, videoGenerationMode]);

  const productSelection = useProductSelection({ selectedAssets, activeScenarioId, mode });
  const {
    asin,
    setAsin,
    manualProductId,
    setManualProductId,
    setManualProductTitle,
    setManualProductImageUrl,
    productName,
    brandName,
    sellingPoints,
    manualProductPromptDismissed,
    setManualProductPromptDismissed,
    fetchedAsins,
    setFetchedAsins,
    fetchedProductIds,
    setFetchedProductIds,
    hasRequiredScenarioInput,
    productSummary,
  } = productSelection;

  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);

  // 動画生成モデル一覧 (Veo / Seedance / 他)。シナリオ情報モーダルとシーン編集
  // ドロップダウン、想定尺の既定値 (モデル最大尺追従) で使う。
  const { data: videoGenerationModels = [] } = useGenerationModels('video');
  // 現在のフェーズ。phase は後方 (useScenarioGeneration) で定義されるため、
  // それより前に走る effect からは ref 経由で最新値を参照する。
  const phaseRef = useRef<'setup' | 'work'>('setup');

  // シナリオ設定フォームの state (localStorage 初期化・相互整合 effect 込み) を集約したフック。
  const form = useScenarioFormState({ promptOptions, videoGenerationModels, phaseRef });
  // ScenarioGenerator 本体でも直接使う値だけを取り出す
  // (SetupPanel 配下へは setupContextValue の `...form` 経由で渡る)。
  const {
    scenarioCount, setScenarioCount,
    additionalPrompt,
    tone, setTone,
    duration, setDuration, durationUserEditedRef, durationSelectOptions,
    setCharacterCountRaw: setCharacterCountState,
    characterCountUserEditedScenarioRef,
    includeNarration, setIncludeNarration,
    includeTelop, setIncludeTelop,
    includeDialogue, setIncludeDialogue,
    autoGenerateFirstFrame,
    autoGenerateStoryboard,
    autoGenerateEndcardFrame, setAutoGenerateEndcardFrame,
    lastFrameMode,
    scenarioGenerationModel, setScenarioGenerationModel,
    thinkingLevel,
    scenarioModel, setScenarioModel,
    excludePeople, setExcludePeople,
    castingGenderFilter, setCastingGenderFilter,
    castingAgeFilter, setCastingAgeFilter,
    setCastingEthnicityFilter,
    videoPrompt, setVideoPrompt,
  } = form;

  // 台本取り込み (docs/features/scenario-script-import-plan.md §4 / §7)。読み込んだ台本の抽出テキストを保持する。
  // 永続化しない (ページ再読込で消える)。取り込み中はシナリオ数を1案として扱う (保存済みの案数は書き換えない)。
  const [importedScript, setImportedScript] = useState<ImportedScriptState | null>(null);
  const effectiveScenarioCount = importedScript ? 1 : scenarioCount;
  // 収録動画から (video-import-motion-scenario-plan.md §3.2)。動画エディタの「この動画からシナリオを作る」が遷移の state で渡す
  const [sourceVideo, setSourceVideo] = useState<SourceVideoState | null>(null);
  const { setMotionGraphics } = useGenerationSettings();

  // 確認ダイアログ群 (モデル切替による俳優クリア / 参考画像モード切替 等)。
  // state と発火 effect はフックが持ち、JSX は ScenarioDialogs が描画する。
  const dialogs = useScenarioConfirmDialogs({
    mode,
    videoGenerationMode,
    setVideoGenerationMode,
    setPromptVideoGenerationMode: setPromptVideoGenMode,
    castingGenderFilter,
    castingAgeFilter,
  });
  const {
    handleVideoGenerationModeChange,
    showClearAllFramesConfirm,
    setShowClearAllFramesConfirm,
  } = dialogs;

  const scenarioHeadingRef = useRef<HTMLHeadingElement>(null);
  const tabsDrag = useScenarioTabsDrag();
  const { shouldSuppressClick } = tabsDrag;

  const handleTabClick = useCallback((e: React.MouseEvent, scenarioId: string) => {
    if (shouldSuppressClick()) {
      e.preventDefault();
      return;
    }
    setActiveScenarioId(scenarioId);
    // タブ切り替えは同一画面内の操作なので先頭へスクロールさせない (ScrollToTop が state を見て抑制)
    navigate(`/studio/video/${scenarioId}`, { replace: false, state: { preserveScroll: true } });
  }, [shouldSuppressClick, setActiveScenarioId, navigate]);

  const { logRecommendationEvent } = useRecommendationEvents();
  // トーン推奨の採用ログ (計画書§8 P4)。ScenarioSettingsSection の HierarchicalSelect
  // onChange (`key === 'none' ? '' : key`) を通った後の最終トーン値を受け取る (setTone
  // と同じ引数形)。選択値が推奨トーンと一致する場合のみ記録する (toneRecommendedKeys は
  // 常に suggested のみを持つ。計画書§5.5参照)。
  const handleToneChange = useCallback((nextTone: string) => {
    setTone(nextTone);
    // 保存値の復元では通知せず、操作で歌モノへ切り替えた場合だけ尺の補正を通知する。
    if (!isLyricsBgmTone(toneHierarchicalOptions, tone) && isLyricsBgmTone(toneHierarchicalOptions, nextTone)) {
      // カスタム値 (「20秒」等) も秒数付きの疑似 option に解決して下限判定にかける (customDuration.ts)
      const currentOption = resolveDurationOption(durationSelectOptions, duration);
      const match = isDurationBelowLyricsMin(currentOption)
        ? findMinLyricsDurationOption(durationSelectOptions)
        : null;
      if (match) {
        durationUserEditedRef.current = true;
        setDuration(match.value);
        toast.show(`歌モノは想定尺 30 秒以上が必要なため「${match.label}」に変更しました`);
      }
    }
    if (nextTone && recommendations?.status === 'ready' && recommendations.tone?.value === nextTone) {
      logRecommendationEvent({
        subject_type: recommendationSubject?.subject_type ?? null,
        subject_id: recommendationSubject?.subject_id ?? null,
        surface: 'scenario_tone',
        action: 'adopted',
        group_key: 'tone',
        option_key: nextTone,
        strength: 'suggested',
        score: recommendations.tone.score,
        profile_status: recommendations.status,
      });
    }
  }, [
    recommendations, recommendationSubject, logRecommendationEvent, setTone,
    toneHierarchicalOptions, tone, durationSelectOptions, duration, durationUserEditedRef, setDuration, toast,
  ]);
  const handleDurationChange = useCallback((v: string) => {
    durationUserEditedRef.current = true;
    setDuration(v);
    // プルダウンでの明示選択だけを次回の初期値として保存する (歌モノ補正などの自動変更は保存しない)
    localStorage.setItem(STORAGE_KEY_DURATION, v);
    // 想定尺推奨の採用ログ (計画書§8 P4)。推奨バッジ付き option の選択時のみ記録。
    if (v && recommendations?.status === 'ready' && recommendations.duration?.value === v) {
      logRecommendationEvent({
        subject_type: recommendationSubject?.subject_type ?? null,
        subject_id: recommendationSubject?.subject_id ?? null,
        surface: 'scenario_duration',
        action: 'adopted',
        group_key: 'duration',
        option_key: v,
        strength: 'suggested',
        score: recommendations.duration.score,
        profile_status: recommendations.status,
      });
    }
  }, [recommendations, recommendationSubject, logRecommendationEvent, setDuration, durationUserEditedRef]);

  // 歌モノ (トーン option の metadata.bgm_mode==='lyrics') は想定尺 30 秒以上が必須
  // (docs/features/scenario-song-lyrics-bgm-plan.md §5.1.4)。
  const isLyricsTone = useMemo(
    () => isLyricsBgmTone(toneHierarchicalOptions, tone),
    [tone, toneHierarchicalOptions],
  );
  // 全編モーション (「モーショングラフィックス」の whole・移行前は映像スタイルの option metadata.production_engine === 'motion'・
  // docs/features/brand-cut-motion-graphics-plan.md §10.5 / §10.14)。生成ボタン直後に BGM を決めるモーダルを挟む。
  const isMotionEngine = useMemo(
    () => resolveMotionEngine({
      motionGraphics, motionGraphicsOptions, visualStyleOptions: visualStyleHierarchicalOptions, visualStyle,
    }),
    [motionGraphics, motionGraphicsOptions, visualStyleHierarchicalOptions, visualStyle],
  );
  // モーションのときは歌モノのトーンを選べなくする (既に選ばれていれば残して警告・送らせない)
  const toneOptionsForSetup = useMemo(
    () => (isMotionEngine ? filterLyricsTonesForMotion(toneHierarchicalOptions, tone) : toneHierarchicalOptions),
    [isMotionEngine, toneHierarchicalOptions, tone],
  );
  // モーションでは「ナレーション」「セリフ」の有無を「声」1 つに畳む (§10.7.5・未決 C)。内部の 2 つの値が食い違っていたら
  // (保存済みの値・切り替え前の値) 表示している「声」の値に揃え、送信する include_narration / include_dialogue を一致させる
  useEffect(() => {
    if (!isMotionEngine) return;
    const voice = deriveMotionVoiceToggle(
      { value: includeNarration, treeEnabled: narrationTreeEnabled, treeOptions: narrationTreeOptions },
      { value: includeDialogue, treeEnabled: dialogueTreeEnabled, treeOptions: dialogueTreeOptions },
    );
    const nextNarration = voiceToBehaviorValue(voice, narrationTreeEnabled, narrationTreeOptions);
    const nextDialogue = voiceToBehaviorValue(voice, dialogueTreeEnabled, dialogueTreeOptions);
    if (nextNarration !== includeNarration) setIncludeNarration(nextNarration);
    if (nextDialogue !== includeDialogue) setIncludeDialogue(nextDialogue);
  }, [isMotionEngine, includeNarration, includeDialogue, narrationTreeEnabled, narrationTreeOptions, dialogueTreeEnabled, dialogueTreeOptions, setIncludeNarration, setIncludeDialogue]);
  // モーションではBGM先行フローがcount=1に固定するため (backendも上書きする)、設定欄の案数も1案に強制し
  // トーストで知らせる。2案以上のまま表示して見積りが案数倍になるのを防ぐ (計画md「複数本のシナリオ (未対応)」)
  useEffect(() => {
    if (!isMotionEngine || scenarioCount === MOTION_SCENARIO_COUNT) return;
    setScenarioCount(MOTION_SCENARIO_COUNT);
    toast.show(MOTION_SCENARIO_COUNT_FORCED_MESSAGE);
  }, [isMotionEngine, scenarioCount, setScenarioCount, toast]);
  // 想定尺 (秒)。全編モーションの BGM の尺 (想定尺 + 余白) の料金見積りに使う
  const motionExpectedSeconds = useMemo(() => {
    const seconds = durationSelectOptions.find((o) => o.value === duration)?.metadata?.seconds;
    if (typeof seconds === 'number') return seconds;
    const m = /(\d+)/.exec(duration || '');
    return m ? Number(m[1]) : null;
  }, [durationSelectOptions, duration]);
  // 全編モーションは全編を 1 本で作れる 60 秒以内に限る (§10.6)。既に 60 秒超が選ばれていれば送らせない
  const isMotionDurationTooLong = useMemo(
    () => isMotionEngine && isDurationOverMotionWholeMax(resolveDurationOption(durationSelectOptions, duration)),
    [isMotionEngine, durationSelectOptions, duration],
  );
  const [motionBgmRequest, setMotionBgmRequest] = useState<null | { submit: (choice: MotionBgmChoice) => void }>(null);
  const requestMotionBgmChoice = useCallback((submit: (choice: MotionBgmChoice) => void) => {
    setMotionBgmRequest({ submit });
  }, []);

  // 保存値の復元や属性ツリーの遅延読み込み時も、想定尺を通知なしで補正する。
  // durationSelectOptions を依存に含めることで、歌モノトーンが
  // 選択済みの状態でマウントし属性ツリー (想定尺の metadata.seconds) が後から届いた
  // 場合にも引き上げが走るようにする (レビュー指摘: isLyricsTone だけでは、tone の
  // ツリーが先に届いて isLyricsTone が true になった時点で duration ツリーが未着なら
  // その後 duration ツリーが届いても再評価されなかった)。
  useEffect(() => {
    if (!isLyricsTone) return;
    const currentOption = resolveDurationOption(durationSelectOptions, duration);
    if (!isDurationBelowLyricsMin(currentOption)) return;
    const match = findMinLyricsDurationOption(durationSelectOptions);
    if (match) {
      // 自動引き上げは明示編集と同等に扱う。useScenarioFormState の「ユーザー未編集なら
      // モデル追従の既定 (15秒) へ寄せる」effect は duration を依存に持つため、この
      // フラグを立てないと 15秒 → 30秒 → 15秒 … と両 effect が往復する (実機で無限ループ)。
      durationUserEditedRef.current = true;
      setDuration(match.value);
    }
  }, [isLyricsTone, duration, durationSelectOptions, setDuration, durationUserEditedRef]);

  // 映像スタイル推奨の採用ログ (scenario_tone / scenario_duration と同じ構え)。
  // ScenarioSettingsSection 側の onChange ('none'→'photorealistic' / ツリー無効時の
  // 正規化) を通った後の最終値を受け取る。
  const handleVisualStyleChange = useCallback((next: string) => {
    setVisualStyle(next);
    // 非写実スタイルへの変更で人間俳優が選択中なら自動解除 (P5-3)。キャラクター俳優は
    // 全スタイルで選択可のため対象外。visualStyleIsStylized はまだ「変更前」の値を
    // 参照している (setVisualStyle は非同期) ため、変更後の stylized 判定をここで計算する。
    const nextIsStylized = resolveVisualStyleStylized(next, visualStyleStylizedMap);
    if (nextIsStylized) {
      const fullCast = [...(selectedCasting ? [selectedCasting] : []), ...additionalCastings];
      const hasHumanActor = fullCast.some((actor) => !isCharacterActor(actor));
      if (hasHumanActor) {
        setSelectedCasting(null);
        setAdditionalCastings([]);
        toast.show('映像スタイルの変更により、選択中のAI俳優を解除しました');
      }
    }
    if (next && recommendations?.status === 'ready' && recommendations.visual_style?.value === next) {
      logRecommendationEvent({
        subject_type: recommendationSubject?.subject_type ?? null,
        subject_id: recommendationSubject?.subject_id ?? null,
        surface: 'scenario_visual_style',
        action: 'adopted',
        group_key: 'visual_style',
        option_key: next,
        strength: 'suggested',
        score: recommendations.visual_style.score,
        profile_status: recommendations.status,
      });
    }
  }, [
    setVisualStyle, recommendations, recommendationSubject, logRecommendationEvent,
    visualStyleStylizedMap, selectedCasting, additionalCastings, setSelectedCasting, setAdditionalCastings, toast,
  ]);

  // videoSettings は useGenerationState の context から取得（localStorage 同期は Provider 側で実施）

  // アスペクト比に基づいて最適な幅を計算
  // 16:9なら横長、9:16なら縦長にする
  const getPreviewWidth = (aspectRatio: string): number => {
    const [width, height] = aspectRatio.split(':').map(Number);
    const ratio = width / height;

    // 横長（16:9など）の場合は幅240pxを基準
    if (ratio >= 1) {
      return 240;
    }
    // 縦長（9:16など）の場合は高さ240pxを基準にして幅を計算
    // 最小幅180px: 「静止画フレームを生成」ボタンが収まる幅
    return Math.max(180, Math.round(240 * ratio));
  };

  // videoSettings の localStorage 同期は GenerationProvider 側で実施

  // 参照画像モード判定（LiteモデルUI制御等に使用）
  const isReferenceImageMode = videoGenerationMode === 'reference';

  // video-to-video (参照動画入力) 判定: フレームモード以外で、動画入力対応モデル
  // (maxReferenceVideos>0) のときに reference_video 素材が選択されているか。
  // 非対応モデルへ切替後は backend が参照動画を無視して通常レートに戻すため、capability を
  // 条件に含めて見積りと backend デポジット/精算を一致させる。
  // フレーム指定 (videoReferenceMode='first_frame'/'last_frame') の動画は参考画像として
  // 送られ reference_videos に入らないため v2v 扱いにしない (payload 組み立てと同条件)。
  const hasReferenceVideo = videoGenerationMode !== 'frame'
    && maxReferenceVideos > 0
    && selectedAssets.some((asset) => asset.usage === 'reference_video' && (asset.videoReferenceMode ?? 'video') === 'video');

  // 選択中の動画モデルが「合算課金モデル」か (DB metadata.aggregates_reference_video_seconds 由来。
  // MiniMax H3 が該当。計画書 §4.1)。モデル id のハードコード分岐はしない。
  const currentVideoModelMetadata = useMemo(
    () => videoGenerationModels.find((m) => m.model_code === videoSettings.videoModel)?.metadata,
    [videoGenerationModels, videoSettings.videoModel],
  );
  const aggregatesReferenceVideoSeconds = (currentVideoModelMetadata as { aggregates_reference_video_seconds?: boolean } | null | undefined)
    ?.aggregates_reference_video_seconds === true;

  // 動画モデル別の参照画像枚数上限 (metadata.max_reference_images。GenerationWorkspace の
  // useVideoModelCapabilities / useVideoHandlers.referenceImageLimitFor と同じ formula)。
  // @メンション候補の絞り込み (mentionEligibleAssets、下記) 専用に、ここでも同じ値を
  // 独自解決する (SelectedAssetsPanel は GenerationWorkspace 側の別インスタンスで
  // computed されるため props 経由では受け取れない)。
  const mentionReferenceImageLimit = useMemo(() => {
    const fromMeta = (currentVideoModelMetadata as { max_reference_images?: number } | null | undefined)?.max_reference_images;
    return typeof fromMeta === 'number' ? fromMeta : 3;
  }, [currentVideoModelMetadata]);

  // 素材 @メンション (docs/features/material-at-mention-plan.md レビュー指摘 P1) の
  // 候補集合: selectedAssets のうち、動画生成の参照上限で実際には送信されない素材
  // (SelectedAssetsPanel のグレーアウト対象と同条件) を除いたもの。プロンプトモードの
  // 動画プロンプト欄 (PromptModePanel) とシーン説明欄 (SceneDetailColumn) の両方が
  // これを候補にする。SceneDetailColumn 側は「シーン単位の実効上限」ではなく、この
  // プロンプトモード基準の上限で近似する (シーンごとの動画モデル/俳優構成が異なり得る
  // ため厳密な集合の算出は複雑になりすぎる。レビュー指摘で許容されている妥協)。
  // 静的ゲート通過後の俳優一覧 (参照上限の切り捨て適用前)。上限コンテキストの
  // actorCount (参照枠を消費する俳優数) の算出にも使うため、先に確定させる。
  const isSeedanceModel = (videoSettings.videoModel || '').startsWith('seedance');
  const gatedMentionActorSources = useMemo(() => filterMentionActorSources(
    buildMentionActorSources({
      casting: selectedCasting,
      additionalCastings,
      seedancePreset: selectedSeedancePresetActor,
      additionalSeedancePresets: additionalSeedancePresetActors,
    }),
    {
      isSeedanceModel,
      isStylized: visualStyleIsStylized,
      includeCastingSheet: videoSettings.includeCastingSheet !== false,
    },
  ), [
    selectedCasting, additionalCastings, selectedSeedancePresetActor, additionalSeedancePresetActors,
    isSeedanceModel, visualStyleIsStylized, videoSettings.includeCastingSheet,
  ]);
  // Seedance (BytePlus) の参照画像下限 (短辺300px) 未満の素材は実送信時に除外される
  // (useVideoHandlers.ts buildVideoPayload / handleGenerateSceneVideo)。SelectedAssetsPanel
  // 側 (同じ mentionUtils 関数を使う別インスタンス) と同じ考え方で、参照上限カウントの
  // 前に「実送信リスト」相当まで絞り込んでおく (小画像が参照枠を無駄に消費して後続の
  // 画像が候補から漏れるのを防ぐ)。
  const mentionCandidateAssets = useMemo(() => (
    isSeedanceModel
      ? selectedAssets.filter((a) => !isBelowMinReferenceSize(a, SEEDANCE_MIN_REFERENCE_IMAGE_SHORT_SIDE))
      : selectedAssets
  ), [selectedAssets, isSeedanceModel]);
  // 参照上限コンテキスト (disabledFromIndex / 参照枠を消費する俳優数・画像参照列基準の
  // 挿入位置)。素材の絞り込みと俳優の切り捨て判定の両方で同じ値を使う。計算は
  // buildMentionReferenceLimitContext (mentionUtils) に一本化 (SelectedAssetsPanel と共有)。
  // フレーム指定の2枠 (最初/最後のフレーム) はプロンプトモードだけに適用する。シナリオの
  // フレームモードは選択素材を参照画像として個別送信しない (フレーム生成の参考と、
  // BE 段階2の素材名解決で使う) ため上限で切らない。SelectedAssetsPanel のグレーアウト
  // (GenerationWorkspace の isPromptFrameMode) と同条件。
  const mentionLimitContext = useMemo(() => {
    const disabledFromIndex = isReferenceImageMode
      ? mentionReferenceImageLimit
      : mode === 'prompt' && videoGenerationMode === 'frame'
        ? 2
        : undefined;
    return buildMentionReferenceLimitContext({
      assets: mentionCandidateAssets,
      gatedActors: gatedMentionActorSources,
      castingPosition,
      disabledFromIndex,
    });
  }, [
    mentionCandidateAssets, gatedMentionActorSources, castingPosition,
    isReferenceImageMode, mode, videoGenerationMode, mentionReferenceImageLimit,
  ]);
  const mentionEligibleAssets = useMemo(
    () => filterAssetsWithinReferenceLimit(mentionCandidateAssets, mentionLimitContext),
    [mentionCandidateAssets, mentionLimitContext],
  );

  // AI俳優 @メンション候補 (docs/features/material-at-mention-plan.md「俳優メンション」節・
  // レビュー指摘 P1)。casting/seedancePreset は排他で、選択中のどちらか。
  // SelectedAssetsPanel の actorItems 構築と同じ順序規則を buildMentionActorSources に
  // 一本化しているため、パネルのバッジ表示とポップアップのバッジ文字は必ず一致する。
  //
  // 静的ゲート (gatedMentionActorSources・上で構築) に加え、CS 方式は参照上限も
  // 消費するため、上限で切り捨てられる俳優を filterActorSourcesWithinReferenceLimit で
  // 除く (mentionEligibleAssets と同じ mentionLimitContext を使用。seedance-preset は
  // asset_id 別送のため上限対象外)。
  const mentionActorSources = useMemo(
    () => filterActorSourcesWithinReferenceLimit(gatedMentionActorSources, mentionLimitContext),
    [gatedMentionActorSources, mentionLimitContext],
  );

  // v2v 課金用の参照動画「合計」尺 (秒)。PromptModePanel の linkedVideoDuration 等が使う
  // 出力設定連動用の「最長」尺 (Math.max) とは別物 — 混同しないこと。
  // hasReferenceVideo と同じ対象 (フレーム指定を除く video モードの reference_video 素材) を合算する。
  // 2026-08-07 ユーザー決定: バックエンドは非整数秒の素材を拒否せず Math.ceil で切り上げて
  // 課金する (reference_video_meta._normalize_duration_seconds)。見積りをバックエンドの
  // 課金値と一致させるため、ここでも素材ごとに ceil してから合算する。
  const referenceVideoSecondsTotal = useMemo(() => {
    if (!hasReferenceVideo) return 0;
    return selectedAssets
      .filter((asset) => asset.usage === 'reference_video' && (asset.videoReferenceMode ?? 'video') === 'video')
      .reduce((sum, asset) => (
        typeof asset.durationSeconds === 'number' && asset.durationSeconds > 0
          ? sum + Math.ceil(asset.durationSeconds)
          : sum
      ), 0);
  }, [hasReferenceVideo, selectedAssets]);

  // 上記のうち、尺が整数秒でない (= 切り上げが発生する) 素材があるか。バックエンドは
  // 拒否せず切り上げて課金するため、警告ではなく「切り上げが発生している」旨の
  // 注記表示にのみ使う (PromptModePanel)。
  const hasCeiledReferenceVideoDuration = useMemo(() => {
    if (!hasReferenceVideo || !aggregatesReferenceVideoSeconds) return false;
    return selectedAssets.some((asset) => (
      asset.usage === 'reference_video'
      && (asset.videoReferenceMode ?? 'video') === 'video'
      && typeof asset.durationSeconds === 'number'
      && asset.durationSeconds > 0
      && !Number.isInteger(asset.durationSeconds)
    ));
  }, [hasReferenceVideo, aggregatesReferenceVideoSeconds, selectedAssets]);

  // プロンプト入力モード用の推定クレジット計算
  const estimatedVideoCredits = useEstimatedVideoCredits(videoSettings, isReferenceImageMode, hasReferenceVideo, referenceVideoSecondsTotal);

  // 「ストーリーボードを自動生成」用ハンドラ。シナリオ生成完了ごとに絵コンテ静止画を
  // シナリオ別キューで自動起動する (selectedAssets / visualStyle は内部で参照)。
  const autoGenerateStoryboards = useAutoStoryboardGeneration({ castingGenderFilter, castingAgeFilter });
  const [lastResult, setLastResult] = useState<ScenarioResponse | null>(null);
  const scenarios = useMemo(() => lastResult?.scenarios ?? [], [lastResult]);
  // 生成済みモーションシーンの BGM 同期の鮮度 (brand-cut-motion-graphics-plan.md §15.4)。SceneVideoBox が注意を出す
  const motionSyncStatus = useMotionSyncStatus(scenarios);
  // 台本取り込みシナリオ: 確認対象 (本文・話者・尺・順序・登場人物等) の更新後に取り込みレポートを取り直す (§6.6.7)
  useImportReportAutoRefresh(lastResult, setLastResult);

  // シナリオ DB キャスト (casting_model_ids) の FE 復元 (修正2-a)。アクティブシナリオが
  // 変わったとき、explicit なローカルキャスト (?model= ディープリンク等) が無ければ
  // DB を正本として selectedCasting/additionalCastings を復元する。
  const { isCastingHydrating } = useCastingHydration({
    activeScenarioId,
    scenarios,
    selectedCasting,
    additionalCastings,
    setSelectedCasting,
    setAdditionalCastings,
    castingSelectionState,
    setCastingSelectionState,
    pendingDeepLinkCastScenarioId,
    consumePendingDeepLinkCast,
  });

  const [expandedConfirmButton, setExpandedConfirmButton] = useState<string | null>(null);
  // 場面継続／場面転換 (continues_previous_shot) の PATCH 進行中シーン (docs/features/
  // link-previous-scene-continuity-default-plan.md §9 step-1 項目7・FE補助)。
  // 保存完了まで該当シーンの動画・フレーム生成ボタンを非活性にし、保存と生成を直列化する。
  const [pendingContinuityScenes, setPendingContinuityScenes] = useState<Set<string>>(new Set());
  const videoDropZoneRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const videoPlayButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map()); // オーバーレイ表示位置用

  // エキスパンドされた確認ボタンを閉じるためのクリックハンドラ
  useEffect(() => {
    if (!expandedConfirmButton) return;

    const handleClickOutside = () => {
      setExpandedConfirmButton(null);
    };

    // 少し遅延させてから登録（ボタンエキスパンド直後のクリックを防ぐ）
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [expandedConfirmButton]);

  const {
    isGlobalDragging,
    isDraggingVideo,
    dragOverLastFrameKey,
    setDragOverLastFrameKey,
    dragOverSceneKey,
    setDragOverSceneKey,
  } = useGlobalDragTracking();

  // 確定素材のselectedAssets追加を1回だけに制限するためのref
  const processedFirstFrameAssets = useRef<Set<string>>(new Set());
  // 画像生成モデル一覧 (GPT Image 2 / Google Gemini)
  // 静止画の媒体要件アコーディオンと同じ DB 駆動ソース (sort_order 順)
  const { data: imageGenerationModels = [] } = useGenerationModels('image');

  const {
    isCreditInsufficient,
    isVideoCreditInsufficient,
    isStorageExceeded,
    scenarioModelCredits,
    creditBadgeText,
  } = useCreditCalculations({
    scenarioGenerationModel,
    settings,
    videoResolution: videoSettings.resolution,
    creditRatesData,
    creditsData,
    estimatedVideoCredits,
    storageData,
  });

  // モデル選択肢の構築・選択中モデルのレート解決・無効化時フォールバック
  const { selectedScenarioModelRate, scenarioModelOptions, scenarioModelDefs, randomRateBadge, currentScenarioModelDef, randomCreditText, randomCreditRange } =
    useScenarioModelOptions({ scenarioModel, setScenarioModel, scenarioModelCredits, scenarioCount: effectiveScenarioCount });

  // 全編モーションのモーダルに出すシナリオの料金 (本数は 1 に固定・ランダムは 1 モデル分のレンジ)
  const motionScenarioCredits = useMemo(() => {
    if (scenarioModel === SCENARIO_MODEL_RANDOM) {
      const rates = scenarioModelDefs.map((d) => d.rate).filter((r): r is number => r !== null);
      return rates.length > 0 ? { min: Math.min(...rates), max: Math.max(...rates) } : null;
    }
    return selectedScenarioModelRate !== null ? { min: selectedScenarioModelRate, max: selectedScenarioModelRate } : null;
  }, [scenarioModel, scenarioModelDefs, selectedScenarioModelRate]);

  // 生成ボタン / 見積に表示する消費目安。ランダムは総額レンジ (文字列)、それ以外は レート×本数。
  const scenarioCreditCost = useMemo<number | string>(() => {
    if (scenarioModel === SCENARIO_MODEL_RANDOM) return randomCreditText ?? '—';
    return selectedScenarioModelRate !== null ? selectedScenarioModelRate * effectiveScenarioCount : effectiveScenarioCount;
  }, [scenarioModel, randomCreditText, selectedScenarioModelRate, effectiveScenarioCount]);

  // シナリオ生成費の残高不足判定。imageCredits ベースの isCreditInsufficient とは別に、
  // シナリオ生成そのものの費用で送信可否をゲートする。
  // - 通常モデル: バックエンドが rate×本数 を一括消費するため、全額必要。
  // - ランダム: best-effort のため、最安 1 モデル分すら払えない (= 必ず全失敗) ときだけブロック。
  //   それ以上あれば作れる分だけ作り、部分成功として正直に報告する (#2 の方針と整合)。
  const scenarioCreditInsufficient = useMemo<boolean>(() => {
    if (!creditsData) return false;
    const balance = creditsData.total_available ?? creditsData.credits_balance;
    if (typeof balance !== 'number') return false;
    if (scenarioModel === SCENARIO_MODEL_RANDOM) {
      if (!randomCreditRange) return false; // レート未ロード時は誤ブロックしない
      return balance < randomCreditRange.minSingle;
    }
    if (selectedScenarioModelRate === null) return false; // レート未ロード時は誤ブロックしない
    return balance < selectedScenarioModelRate * effectiveScenarioCount;
  }, [creditsData, scenarioModel, randomCreditRange, selectedScenarioModelRate, effectiveScenarioCount]);

  // プロンプトモード用の状態
  const [promptVideoGenerated, setPromptVideoGenerated] = useState(false);
  const [promptVideoMaterialId, setPromptVideoMaterialId] = useState<string | null>(null);
  const promptVideoDisplayRef = useRef<HTMLDivElement>(null);

  // プロンプトモードの動画生成完了時にプレビューへスムーズスクロール
  useEffect(() => {
    if (promptVideoGenerated && promptVideoMaterialId) {
      const id = setTimeout(() => {
        promptVideoDisplayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(id);
    }
  }, [promptVideoGenerated, promptVideoMaterialId]);
  const [previewModalMaterial, setPreviewModalMaterial] = useState<any | null>(null);
  const [previewModalSceneId, setPreviewModalSceneId] = useState<string | null>(null);

  const scenarioInfoModal = useScenarioInfoModal();
  const bgm = useBgmEditor({ scenarios, activeScenarioId });
  const [expandedNarrations, setExpandedNarrations] = useState<Record<string, boolean>>({});
  const [expandedVideos, setExpandedVideos] = useState<Record<string, boolean>>({});

  const updateScenarioMutation = useUpdateScenario();

  // 参照画像モード: selectedAssetsの変更をscenariosテーブルに同期
  // activeScenarioIdが変わった直後はスコープ切替中のためスキップ
  const prevRefAssetsRef = useRef<string>('');
  const prevRefScenarioIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (videoGenerationMode !== 'reference' || !activeScenarioId) return;
    // シナリオ切替直後はスキップ（次回のassets変更から同期開始）
    if (prevRefScenarioIdRef.current !== activeScenarioId) {
      prevRefScenarioIdRef.current = activeScenarioId;
      const referenceAssets = selectedAssets
        .filter((a) => (a.usage ?? 'reference') === 'reference')
        .map((a) => ({ id: a.id, sourceUrl: a.sourceUrl || '', thumbnailUrl: a.thumbnailUrl || '' }));
      prevRefAssetsRef.current = JSON.stringify(referenceAssets);
      return;
    }
    const referenceAssets = selectedAssets
      .filter((a) => (a.usage ?? 'reference') === 'reference')
      .map((a) => ({ id: a.id, sourceUrl: a.sourceUrl || '', thumbnailUrl: a.thumbnailUrl || '' }));
    const key = JSON.stringify(referenceAssets);
    if (key === prevRefAssetsRef.current) return;
    prevRefAssetsRef.current = key;
    updateScenarioMutation.mutate({
      scenarioId: activeScenarioId,
      payload: { reference_image_urls: referenceAssets },
    });
  }, [selectedAssets, videoGenerationMode, activeScenarioId, updateScenarioMutation]);

  // アコーディオンの開閉状態（フック呼び出しより前に定義が必要）
  const accordions = useSetupAccordions();
  const { setIsProductSectionOpen } = accordions;

  // --- changePhase ref (changePhase is defined later; use ref for deferred binding) ---
  const changePhaseFnRef = useRef<((phase: 'setup' | 'work') => void) | null>(null);
  const changePhaseStable = useCallback((phase: 'setup' | 'work') => {
    changePhaseFnRef.current?.(phase);
  }, []);

  // 公開確認モーダル (採用素材の共有移行) を共通フックに集約。公開導線はこれに委譲する。
  const { requestPublish: requestPublishScenario, dialog: publishConfirmDialog } = usePublishConfirm({
    onPublished: (scenarioId) => {
      setLastResult(prev => prev ? {
        ...prev,
        scenarios: prev.scenarios.map(s => s.id === scenarioId ? { ...s, published: true } : s),
      } : prev);
    },
  });

  // --- useSceneActions hook ---
  // シーン削除時の選択中素材の片付けは生成フレームに限る (ユーザーが選んだ素材は残す)
  const removeGeneratedFrameAsset = useCallback((id: string) => {
    if (isGeneratedSceneFrameAssetId(selectedAssets, id)) removeAsset(id);
  }, [selectedAssets, removeAsset]);

  const {
    editingField,
    setEditingField,
    editingValue,
    setEditingValue,
    deletingScenes,
    addingScenes,
    swappingScenes,
    handleGenerateScenePreviewRef,
    editingDialogueLinesRef,
    editingTelopLinesRef,
    createSceneMutation,
    deleteSceneMutation,
    updateSceneMutation,
    aiContinueMutation,
    productLastCutMutation,
    logoLastCutMutation,
    titleCardMutation,
    brandCutFromMaterialMutation,
    handleAddScene,
    handleAiContinueScene,
    handleProductLastCut,
    handleLogoLastCut,
    handleTitleCard,
    handleBrandCutFromMaterial,
    handleDeleteScene,
    handleSwapSceneOrder,
    handleSaveField,
    handleToggleStar,
    handleTogglePublish,
    handleDeleteScenario,
    handleCloseScenarioTab,
  } = useSceneActions({
    lastResult,
    setLastResult,
    activeScenarioId,
    setActiveScenarioId,
    scenarioIdFromPath,
    changePhase: changePhaseStable,
    removeGeneratedFrameAsset,
    setAddSceneModalScenarioId,
    setAddSceneGeneratingLabel,
    scenarioModel,
    autoGenerateEndcardFrame,
    videoGenerationMode,
    requestPublish: requestPublishScenario,
  });

  // シーン追加(全ルート)生成中に、ストーリーボード/シーン一覧へ差し込むスケルトン。
  // DB待ちが長いため、モーダルを即閉じしてもプレースホルダーで進捗を示す。
  // position は挿入位置 (start=冒頭タイトルカード / end=末尾)。
  // react-query の result は毎レンダー別参照のため useMemo せず素で組み立てる (計算は軽量)。
  const pendingSceneSkeletons: { scenarioId: string; position: 'start' | 'end'; label: string }[] = [];
  {
    const pushEnd = (m: { isPending: boolean; variables?: { scenarioId?: string } }, label: string) => {
      if (m.isPending && m.variables?.scenarioId) {
        pendingSceneSkeletons.push({ scenarioId: m.variables.scenarioId, position: 'end', label });
      }
    };
    // 冒頭(タイトルカード)
    if (titleCardMutation.isPending && titleCardMutation.variables?.scenarioId) {
      pendingSceneSkeletons.push({ scenarioId: titleCardMutation.variables.scenarioId, position: 'start', label: 'タイトルカードを生成中...' });
    }
    // 末尾
    pushEnd(logoLastCutMutation, 'エンドカードを生成中...');
    pushEnd(productLastCutMutation, '商品カットを生成中...');
    pushEnd(aiContinueMutation, 'シーンを生成中...');
    // 「シーンを下に追加」(after_scene_id あり) は途中挿入なので末尾スケルトンは出さない
    if (!(createSceneMutation.variables as { payload?: { after_scene_id?: string } } | undefined)?.payload?.after_scene_id) {
      pushEnd(createSceneMutation, 'シーンを追加中...');
    }
    // 既存素材から継ぎ足し (冒頭/末尾は variables.position による)
    const bc = brandCutFromMaterialMutation;
    if (bc.isPending && bc.variables?.scenarioId) {
      const pos = bc.variables.position;
      pendingSceneSkeletons.push({
        scenarioId: bc.variables.scenarioId,
        position: pos,
        label: pos === 'start' ? 'タイトルカードを追加中...' : 'エンドカードを追加中...',
      });
    }
  }

  // --- useScenarioExport hook ---
  const {
    handleDownloadMaterial,
    handleDownloadScenario,
    handleCopyScenarioJson,
    downloadingMaterials,
    isDownloading,
  } = useScenarioExport();

  // --- useNarrationHandlers hook ---
  const {
    selectedNarrationProvider,
    setSelectedNarrationProvider,
    restoreNarrationProvider,
    recommendedNarrator,
    setRecommendedNarrator,
    narratorAccent,
    narrationEnglishScenes,
    setNarrationEnglishScenes,
    autoGenerateNarration,
    setAutoGenerateNarration,
    voicesData,
    voicesLoading,
    voiceNameMap,
    handleSelectNarration,
    handleSetAudioTrackType,
    handleSelectSoundLogo,
    handleToggleNarrationExcluded,
    handleMergeComplete,
  } = useNarrationHandlers({
    lastResult,
    setLastResult,
    activeScenarioId,
    scenarios,
    dialectVoiceAccents,
  });

  // --- useFrameHandlers hook ---
  // シーン単位のフレーム生成設定 (追加プロンプト等)。保存の直列化と flush (scene-frame-generation-tuning-plan.md §6.7)
  const sceneFrameSettings = useSceneFrameSettings({
    updateScene: updateSceneMutation.mutateAsync,
    setLastResult,
  });

  const {
    batchGeneratingFrames,
    batchFrameCancelRefs,
    showBatchCancelModal,
    setShowBatchCancelModal,
    handleGenerateScenePreview,
    collectSceneFrameReferenceCandidates,
    ensureAddedMaterialsResolved,
    forgetAddedMaterial,
    handleConfirmFirstFrame,
    handleClearFirstFrame,
    handleClearAllFrames,
    handleDropMaterial,
    handleDropLastFrameMaterial,
    handleRemoveLastFrame,
    handleRestoreLastFrame,
    handleGenerateLastFrame,
    handleBatchGenerateFirstFrames,
  } = useFrameHandlers({
    lastResult,
    setLastResult,
    scenarios,
    flushSceneFrameSettings: sceneFrameSettings.flushSceneFrameSettings,
    setExpandedConfirmButton,
    processedFirstFrameAssets,
    form,
    product: productSelection,
    isCreditInsufficient,
    isStorageExceeded,
    openPurchaseModal,
    tempMaterials,
    refetchCredits,
  });
  handleGenerateScenePreviewRef.current = handleGenerateScenePreview;

  // 「商品画像を再生成」(StoryboardCandidates) で合成・アップロードした統合タイル画像。
  // useVideoHandlers がストーリーボード動画生成時の商品画像添付として参照する。
  // ハンドラ本体は handleRegenerateProductTile (handleDownloadAssetTile の近く) を参照。
  const [isRegeneratingProductTile, setIsRegeneratingProductTile] = useState(false);
  // signature: タイルを合成した時点の selectedAssets の同一性指紋。動画生成時に
  // 「選択が変わったら作り直す」差分判定に使う。
  const [storyboardProductTile, setStoryboardProductTile] = useState<{ materialId: string; url: string; signature: string } | null>(null);

  // タイルに焼き込む表示名 (商品名 / ブランド名)。build とシグネチャで同じ値を使い、
  // 内容とシグネチャが必ず一致するようにする。
  const storyboardTileDisplayNames = useMemo(() => {
    const ci = lastResult?.custom_input;
    return {
      productName: (ci?.product_name || productSummary || productName).trim(),
      brandName: (ci?.brand_name || activeScenarioBrand.data?.brand_name || brandName).trim(),
    };
  }, [lastResult, productSummary, productName, activeScenarioBrand.data, brandName]);

  // タイルの同一性シグネチャ。タイル画像の内容を左右する要素をすべて含める:
  // シナリオID + 焼き込む商品名/ブランド名 + 選択素材ID。これにより、別シナリオ・別商品で
  // 偶然同じ素材IDの組み合わせになっても、古い商品名/ブランド名入りタイルを再利用しない。
  // 覆面化を実行した選択素材。合成時に元画像から覆面化画像へ差し替わるため、
  // タイル合成後に覆面化した場合は signature を変えて作り直させる。
  const sanitizedSelectedAssetKeys = useMemo(
    () => selectedAssets
      .filter((a) => facePrivacySanitizedIds.includes(a.id))
      .map((a) => `sanitized:${a.id}`),
    [selectedAssets, facePrivacySanitizedIds],
  );
  const storyboardTileSignature = useMemo(
    () => [
      activeScenarioId ?? '',
      storyboardTileDisplayNames.productName,
      storyboardTileDisplayNames.brandName,
      ...selectedAssets.map((a) => a.id),
      ...sanitizedSelectedAssetKeys,
    ].join('|'),
    [activeScenarioId, storyboardTileDisplayNames, selectedAssets, sanitizedSelectedAssetKeys],
  );

  // 現在の selectedAssets から統合タイル画像を 1 枚合成し、一時素材としてアップロードして
  // storyboardProductTile に保存する。signature に合成元のシグネチャを焼き込む。
  // 成功時は保存したタイル、失敗 / 選択素材ゼロのときは null を返す。
  // 「商品画像を再生成」ボタンと、動画生成直前の鮮度保証 (ensureStoryboardProductTile) の両方から使う。
  const buildStoryboardProductTile = useCallback(async (): Promise<{ materialId: string; url: string; signature: string; sanitizedCount: number } | null> => {
    if (selectedAssets.length === 0) return null;
    // 覆面化画像が用意済みの素材はそちらで合成する。統合画像は合成後 1 枚の画像に
    // なるため、個別素材のようにバックエンド側で顔なし画像へ差し替えられない。
    const tile = await renderSelectedAssetsTileBlob(selectedAssets, {
      productName: storyboardTileDisplayNames.productName,
      brandName: storyboardTileDisplayNames.brandName,
      footerLabel: `${brandConfig.labels.productShort}画像`,
    }, { preferFacePrivacySanitized: true });
    if (!tile) return null;
    const file = new File([tile.blob], `storyboard-product-${tile.count}.png`, { type: 'image/png' });
    const uploaded = await uploadTemporaryMaterial({ file, source_type: 'storyboard_attached_tile' });
    const url = await fetchMaterialSignedUrl(uploaded.id, true);
    const next = { materialId: uploaded.id, url, signature: storyboardTileSignature };
    setStoryboardProductTile(next);
    // 旧タイルはここでは消さない。キュー投入済み・未処理の動画ジョブが旧タイルの署名URLを
    // 後から読むケースで参照画像が消えるため (backend は添付URLがあると商品メイン画像
    // フォールバックを無効化する)。掃除はジョブが使い終わった後段に任せる。
    return { ...next, sanitizedCount: tile.sanitizedCount };
  }, [selectedAssets, storyboardTileSignature, storyboardTileDisplayNames]);

  // 動画生成直前に呼ぶ鮮度保証。タイルが現在の選択素材と一致していれば再合成せずそのまま返し
  // (= 無変更ジョブを遅くしない)、選択が増減していれば作り直す。選択素材ゼロは null
  // (= backend が DB 商品メイン画像にフォールバック)。
  const ensureStoryboardProductTile = useCallback(async (): Promise<{ materialId: string; url: string } | null> => {
    if (selectedAssets.length === 0) return null;
    if (storyboardProductTile && storyboardProductTile.signature === storyboardTileSignature) {
      return storyboardProductTile;
    }
    return await buildStoryboardProductTile();
  }, [selectedAssets, storyboardTileSignature, storyboardProductTile, buildStoryboardProductTile]);

  // --- useVideoHandlers hook ---
  const {
    setSceneVideoSettings,
    noFrameConfirmOpen,
    noFrameConfirmDontShow,
    setNoFrameConfirmDontShow,
    frameMismatchConfirm,
    setFrameMismatchConfirm,
    showVideoPromptHistory,
    setShowVideoPromptHistory,
    handleSelectVideoPromptHistory,
    videoGenerationMutation,
    handleGenerateVideoFromPrompt,
    motionLayeredPrompt,
    handleGenerateSceneVideo,
    handleGenerateSwipeClip,
    handleGenerateStoryboardMotionOverlay,
    handleGenerateWholeMotion,
    handleReviseWholeMotion,
    wholeMotionRunning,
    wholeMotionStage,
    handleSelectVideo,
    handleToggleVideoExcluded,
    handleRemoveVideoCard,
    getSceneVideoSettings,
    updateSceneVideoSettings,
    applyScenarioVideoSettingsToScenes,
    getSceneStoryboardDuration,
    updateSceneStoryboardDuration,
    applySceneStoryboardDurations,
    updateSceneBrandCutDuration,
    handleFrameMismatchSelect,
    handleFrameMismatchConfirm,
    handleNoFrameConfirm,
    handleNoFrameCancel,
    aspectMismatchConfirmOpen,
    handleAspectMismatchConfirm,
    handleAspectMismatchChangeAndConfirm,
    handleAspectMismatchCancel,
  } = useVideoHandlers({
    mode,
    form,
    videoGenerationMode,
    ensureStoryboardProductTile,
    isVideoCreditInsufficient,
    isStorageExceeded,
    isCastingHydrating,
    creditRatesData,
    creditsData,
    openPurchaseModal,
    refetchCredits,
    lastResult,
    setLastResult,
    scenarios,
    setPromptVideoGenerated,
    setPromptVideoMaterialId,
    handleConfirmFirstFrame,
    tempMaterials,
    cloudMaterials,
  });

  // --- 声の台本 (motion-v2) の声の生成 / 撮り直し (§10.7.3・2g-4) ---
  const { handleGenerateVoiceLines, handleRetakeVoiceLine, refreshVoiceScenario, voiceLinesRunning, voiceLinesLastResult } = useVoiceLineHandlers({
    scenarios,
    setLastResult,
    openPurchaseModal,
    refetchCredits,
  });

  const duplicateScenarioMutation = useDuplicateScenario();

  // --- useScenarioGeneration hook ---
  const {
    mutation,
    handleLoadScenario,
    handleLoadScenarios,
    handleClearScenario,
    handleSubmit,
    phase,
    hasTransitioned,
    changePhase,
    isLoadingScenario,
  } = useScenarioGeneration({
    scenarioIdFromPath,
    currentUserId: user?.user_uid,
    lastResult,
    setLastResult,
    mode,
    setMode,
    product: productSelection,
    form,
    videoGenerationMode,
    setVideoGenerationModeTransient,
    setExpandedVideos,
    setExpandedNarrations,
    setIsProductSectionOpen,
    setSceneVideoSettings,
    setRecommendedNarrator,
    tempMaterials,
    productSelectRef,
    setShowProductTooltip,
    handleBatchGenerateFirstFrames,
    autoGenerateStoryboards,
    isMotionEngine,
    isLyricsTone,
    isMotionDurationTooLong,
    requestMotionBgmChoice,
    importedScript,
    sourceVideo,
  });

  // 収録動画から (§3.2 の 3): 遷移の state を 1 回だけ読み、案件 (紐づけ済みなら manual・無ければカスタム入力)・想定尺・
  // モーショングラフィックス「全編」を初期値にする。state は消費して再読込で繰り返さない
  const sourceVideoAppliedRef = useRef(false);
  const sourceVideoDurationRef = useRef<string | null>(null);
  useEffect(() => {
    if (sourceVideoAppliedRef.current) return;
    const sv = readSourceVideoState(location.state);
    if (!sv) return;
    // 想定尺の選択肢が揃ってから適用する (揃う前に入れると復元の検証で捨てられ既定に戻る)
    if (durationSelectOptions.length === 0) return;
    sourceVideoAppliedRef.current = true;
    // 要点の既定は想定尺 (初期値 30 秒) に応じた上位 N 件 (video-import-scenario-key-points-plan.md §5.2)
    setSourceVideo(initialSourceVideoState(sv, SOURCE_VIDEO_DEFAULT_DURATION_SEC));
    // 選択中素材は捨て、紐づけた案件の画像を取り直す (案件選択モーダルと同じ扱い。取得済みの印を消さないと、
    // 同じ案件を前に選んでいたときに画像が戻らない)
    clearAssets();
    // 案件の入れ替えは次のティックで行う: 素材を消した描画で「商品素材が外された」扱いの effect (useProductSelection) が
    // 走り、同じ描画で入れた案件を消してしまう (遷移時に案件が選択状態にならない。利用者指摘 2026-11-05)
    const applyProduct = () => {
      if (sv.asin) {
        // Amazon 案件: ASIN モードで開く (案件選択モーダルの Amazon 側と同じ扱い。取得済みの印を消して画像を取り直す)
        const nextAsin = sv.asin;
        setFetchedAsins((prev) => {
          const next = new Set(prev);
          next.delete(nextAsin);
          return next;
        });
        setAsin(nextAsin);
        setManualProductId(null);
        setManualProductTitle(null);
        setManualProductImageUrl(null);
        setScenarioInputTab('product');
        setMode('asin');
      } else if (sv.productId) {
        const nextProductId = sv.productId;
        setFetchedProductIds((prev) => {
          const next = new Set(prev);
          next.delete(nextProductId);
          return next;
        });
        setManualProductId(sv.productId);
        setManualProductTitle(sv.productName || null);
        setManualProductImageUrl(null);
        setAsin('');
        setScenarioInputTab('product');
        setMode('manual');
      } else {
        // 案件なし: 案件情報の手入力ウィンドウ (ProductSection の「手入力」と同じ) に案件名を入れて開く
        setManualProductId(null);
        setManualProductTitle(null);
        setAsin('');
        productSelection.setProductName(sv.productName);
        setScenarioInputTab('manual');
        setMode('custom');
      }
    };
    window.setTimeout(applyProduct, 0);
    const durationLabel = `${SOURCE_VIDEO_DEFAULT_DURATION_SEC}秒`;
    const option = durationSelectOptions.find((o) => o.label === durationLabel || o.value === durationLabel);
    // 利用者の明示編集と同じ扱いにして、動画モデル追従の既定スナップで戻されないようにする。
    // 値は下の effect で入れる (同じ flush で走る想定尺の正規化 effect に上書きされないよう、state が揃うまで入れ直す)
    durationUserEditedRef.current = true;
    sourceVideoDurationRef.current = option?.value ?? durationLabel;
    setMotionGraphics(MOTION_GRAPHICS_WHOLE);
    window.history.replaceState({}, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 遷移の state を 1 回だけ適用する
  }, [location.state, durationSelectOptions]);
  useEffect(() => {
    const pending = sourceVideoDurationRef.current;
    if (pending == null) return;
    if (duration === pending) {
      sourceVideoDurationRef.current = null;
      return;
    }
    durationUserEditedRef.current = true;
    setDuration(pending);
  }, [duration, durationSelectOptions, setDuration, durationUserEditedRef]);
  // 想定尺を source_video.duration_sec に同期する。利用者が要点を触っていなければ既定のチェックも入れ直す (§5.2)
  useEffect(() => {
    const sec = durationSecondsOf(duration, durationSelectOptions);
    if (sec == null) return;
    setSourceVideo((prev) => (prev ? syncSourceVideoDuration(prev, sec) : prev));
  }, [duration, durationSelectOptions]);
  // シナリオを複製し、新シナリオをタブとして開く
  const handleDuplicateScenario = useCallback(async (scenarioId: string) => {
    try {
      const created = await duplicateScenarioMutation.mutateAsync(scenarioId);
      toast.success('シナリオを複製しました');
      await handleLoadScenario(created.id);
    } catch (err) {
      toast.error(getResponseErrorMessage(err) || 'シナリオの複製に失敗しました');
    }
  }, [duplicateScenarioMutation, toast, handleLoadScenario]);
  changePhaseFnRef.current = changePhase;
  phaseRef.current = phase; // 想定尺の既定スナップ effect (useScenarioFormState) から参照するため phase を ref に同期

  // beginVideoEdit で store のスコープと素材は同期済み。ここでは ScenarioGenerator 内部の
  // 表示 mode/phase だけを手動切替時と同じ状態に揃える。
  useEffect(() => {
    if (!pendingVideoEditEntry) return;
    setMode('prompt');
    onModeChange?.('prompt');
    if (phase === 'work') {
      changePhase('setup');
    }
    setActiveScenarioId(null);
    clearVideoEditEntry();
  }, [pendingVideoEditEntry, phase, changePhase, onModeChange, setActiveScenarioId, clearVideoEditEntry]);

  // 動画ジョブ履歴: プレビューを開く
  const handleVideoHistoryPreview = useCallback(async (materialId: string) => {
    const tempItems = tempMaterials.data?.pages.flatMap((page) => page.items) || [];
    const cloudItems = cloudMaterials.data?.pages.flatMap((page) => page.items) || [];
    const cached = [...tempItems, ...cloudItems].find((m) => m.id === materialId);
    if (cached) {
      setPreviewModalMaterial(cached);
      return;
    }
    const fetched = await fetchMaterialById(materialId);
    if (fetched) setPreviewModalMaterial(fetched);
  }, [tempMaterials.data, cloudMaterials.data]);

  // 動画ジョブ履歴: プロンプト・設定を単発動画モードに復元
  const handleRestoreVideoSettings = useCallback((payload: VideoJobRestorePayload) => {
    if (payload.prompt_text) {
      setVideoPrompt(payload.prompt_text);
    }
    setVideoSettings((prev) => {
      const next: VideoSettings = { ...prev };
      if (payload.video_model) next.videoModel = payload.video_model as VideoSettings['videoModel'];
      if (payload.resolution) {
        const resStr = String(payload.resolution);
        if (resStr === '720' || resStr === '1080') next.resolution = resStr;
      }
      if (payload.aspect_ratio) {
        const ar = payload.aspect_ratio;
        if (ar === '16:9' || ar === '9:16') next.aspectRatio = ar;
      }
      if (payload.duration_seconds === 4 || payload.duration_seconds === 6 || payload.duration_seconds === 8) {
        next.duration = payload.duration_seconds;
      }
      // generate_audio は復元しない (音声生成は常時オン。トグルは 2026-09-14 に撤廃)
      if (payload.resize_mode === 'pad' || payload.resize_mode === 'crop') next.resizeMode = payload.resize_mode;
      return next;
    });

    // 素材復元: 新メタデータ (has_asset_info=true) のみ。旧データは現在の選択を触らない。
    if (payload.has_asset_info) {
      const firstId = payload.first_frame_material_id || null;
      const lastId = payload.last_frame_material_id || null;
      const refIds = payload.reference_material_ids ?? [];
      const refVideoIds = payload.reference_video_material_ids ?? [];

      // 復元先モード: frame 系IDがあれば frame、そうでなければ reference。
      // 参照動画 (reference_video) は reference モードと共存する (frame とは排他)。
      const restoreMode: VideoGenerationMode = (firstId || lastId) ? 'frame' : 'reference';
      setPromptVideoGenMode(restoreMode);

      const entries: Array<{ id: string; usage: GenerationAssetUsage }> = [];
      if (restoreMode === 'frame') {
        if (firstId) entries.push({ id: firstId, usage: 'first_frame' });
        if (lastId) entries.push({ id: lastId, usage: 'last_frame' });
      } else {
        for (const id of refIds) {
          entries.push({ id, usage: 'reference' });
        }
        for (const id of refVideoIds) {
          entries.push({ id, usage: 'reference_video' });
        }
      }

      if (entries.length === 0) {
        replaceAssets([]);
      } else {
        void (async () => {
          const results = await Promise.all(entries.map((e) => fetchMaterialById(e.id).catch(() => null)));
          const restored: GenerationAsset[] = [];
          results.forEach((mat, i) => {
            if (!mat) return;
            const urls = resolveMaterialUrls(mat);
            const dragData = buildDragData(mat, urls) as Record<string, unknown>;
            restored.push({ ...(dragData as unknown as GenerationAsset), usage: entries[i].usage });
          });
          replaceAssets(restored);
          const missing = entries.length - restored.length;
          if (missing > 0) {
            toast.show(`一部の素材は復元できませんでした（削除済み: ${missing}件）`);
          }
        })();
      }
    }

    toast.success('動画設定を復元しました');
  }, [setVideoPrompt, setVideoSettings, setPromptVideoGenMode, replaceAssets, toast]);

  // トップページから state.autoGenerate=true で遷移してきた場合、
  // videoPrompt がセットされ次第 1 度だけ自動生成を発火する
  const autoGenerateVideoTriggeredRef = useRef(false);
  // 3 タブ目「モーショングラフィックス」が出ているか (PromptModePanel と同じ判定)
  const motionTabVisible = useMotionGraphicsTabVisible();
  useEffect(() => {
    if (autoGenerateVideoTriggeredRef.current) return;
    if (mode !== 'prompt') return;
    if (!location.state || !(location.state as { autoGenerate?: boolean }).autoGenerate) return;
    if (!videoPrompt.trim()) return;
    if (videoGenerationMutation.isPending) return;
    // モーショングラフィックス中は自動で生成しない (レイヤー素材 1 件が必須で、定額のポイントを
    // 利用者の確認なしに使わない・docs/features/motion-graphics-mode-plan.md §5.4)。判定は resolvePromptAutoGenerateAction。
    const autoAction = resolvePromptAutoGenerateAction({ videoGenerationMode, motionTabVisible });
    if (autoAction === 'wait') return;  // availability の取得中、またはタブを出さない条件で reference へ寄せられる途中
    autoGenerateVideoTriggeredRef.current = true;
    window.history.replaceState({}, '');
    if (autoAction === 'motionGuide') {
      // autoGenerate は消費済み (後で AIおまかせ を押した瞬間に生成が走らないように)
      toast.show(MOTION_LAYERED_AUTO_GENERATE_NOTICE);
      return;
    }
    handleGenerateVideoFromPrompt();
  }, [videoPrompt, mode, location.state, videoGenerationMutation.isPending, handleGenerateVideoFromPrompt, videoGenerationMode, motionTabVisible, toast]);


  // アコーディオンの開閉状態（定義はフック呼び出しブロック前に移動済み）
  // プロンプト入力モード用のアコーディオン状態

  // キャスティングフィルター状態（定義は Seedance 顔警告 useEffect より前に移動済み）

  // キャスティング用モデル取得
  const castingFilters = useMemo(() => {
    const filters: { gender?: string; age?: number } = {};
    if (castingGenderFilter && castingGenderFilter !== 'none') filters.gender = castingGenderFilter;
    if (castingAgeFilter !== null) filters.age = castingAgeFilter;
    return filters;
  }, [castingGenderFilter, castingAgeFilter]);

  const castingModelsQuery = usePromptModels(castingFilters, { enabled: mode !== 'prompt' });

  // URLパラメータ(?model=)でキャスティングを事前選択（AI俳優一覧「動画にキャスティング」からの遷移）。
  // 一覧表示用の castingModelsQuery (性別・年代フィルタ付き) から find すると、フィルタが
  // 対象俳優と矛盾する場合に黙って不発になるため、ids 指定の専用クエリで単体解決する
  // (docs/features/character-casting-consistency-plan.md 修正1-a)。
  const modelParam = searchParams.get('model');
  const castingParamQuery = usePromptModels(
    modelParam ? { ids: [modelParam] } : {},
    { enabled: Boolean(modelParam) },
  );
  // ref のブール値ではなく処理済み ID を保持し、model パラメータが変わったら再処理できるようにする
  const castingParamProcessedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!modelParam || castingParamProcessedIds.current.has(modelParam)) return;

    const outcome = resolveModelParamQueryOutcome(modelParam, {
      data: castingParamQuery.data,
      isError: castingParamQuery.isError,
    });
    if (outcome.kind === 'loading') return;
    if (outcome.kind === 'error') {
      castingParamProcessedIds.current.add(modelParam);
      toast.error('AI俳優情報の取得に失敗しました');
      return;
    }
    if (outcome.kind === 'not_found') {
      castingParamProcessedIds.current.add(modelParam);
      toast.error('指定された俳優が見つかりませんでした');
      return;
    }

    const target = outcome.target;
    const plan = planCastingParamSelection(target, {
      videoModel: videoSettings.videoModel,
      genderFilter: castingGenderFilter,
      ageFilter: castingAgeFilter,
      excludePeople,
    });

    if (plan.switchModelFromSeedance) {
      if (videoGenerationModels.length === 0) return; // モデル一覧の読み込み待ち
      const castingModels = videoGenerationModels.filter(
        (m) => !m.model_code?.startsWith('seedance')
          && (m.supported_modes ?? []).includes('r2v'),
      );
      const castingDefault = castingModels.find((m) => m.is_default)
        ?? castingModels.find((m) => m.is_recommended)
        ?? castingModels[0];
      if (castingDefault) {
        // Seedance 専用の俳優選択をクリアしてから切替（確認ダイアログを出さない）
        setSelectedSeedancePresetActor(null);
        setAdditionalSeedancePresetActors([]);
        setVideoSettings((prev) => ({ ...prev, videoModel: castingDefault.model_code as VideoSettings['videoModel'] }));
      }
    }

    castingParamProcessedIds.current.add(modelParam);
    setCastingEthnicityFilter(plan.ethnicityFilter);
    if (plan.resetGenderFilter) setCastingGenderFilter('none');
    if (plan.resetAgeFilter) setCastingAgeFilter(null);
    if (plan.resetExcludePeople) setExcludePeople(false);

    setSelectedCasting({
      id: target.id,
      display_name: target.display_name,
      face_image_public_url: target.face_image_public_url,
      character_sheet_public_url: target.character_sheet_public_url,
      gender: target.gender,
      min_age: target.min_age,
      max_age: target.max_age,
      ethnicity: target.ethnicity,
    });
    // ?model= ディープリンク由来のキャストはユーザーの明示操作として扱う。加えて
    // pending-deep-link の適用対象シナリオをセットし、DB (casting_model_ids) 復元
    // (修正2-a) をスキップして保持できるようにする (docs/features/
    // character-casting-consistency-plan.md 修正2-a「修正1との相互作用」精緻化・
    // 2026-08-20 実装レビュー P1×2)。単なる explicit 判定だけで preserve すると、
    // シナリオ A で通常編集したキャストが B へ持ち越されてしまうため区別する。
    // シナリオ表示中 (activeScenarioId あり) はその場適用済みなので pending を立てない
    // (立てたままだと次のシナリオ切替で消費されずに持ち越されてしまう)。シナリオ未選択
    // なら次に開くシナリオへ一度だけ適用する PENDING_NEXT_SCENARIO をセットする。
    markCastingExplicit();
    setPendingDeepLinkCastScenarioId(resolveDeepLinkCastPendingTarget(activeScenarioId));
    accordions.setIsCastingSectionOpen(true);

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('model');
      return next;
    }, { replace: true });
  }, [
    modelParam,
    castingParamQuery.data,
    castingParamQuery.isError,
    videoGenerationModels,
    videoSettings.videoModel,
    castingGenderFilter,
    castingAgeFilter,
    excludePeople,
    activeScenarioId,
    setSelectedCasting,
    markCastingExplicit,
    setPendingDeepLinkCastScenarioId,
    setSelectedSeedancePresetActor,
    setAdditionalSeedancePresetActors,
    setVideoSettings,
    setCastingEthnicityFilter,
    setCastingGenderFilter,
    setCastingAgeFilter,
    setExcludePeople,
    setSearchParams,
    accordions,
    toast,
  ]);

  const asinDisabled = mutation.isPending && mode === 'asin';
  const customDisabled = mutation.isPending && mode === 'custom';
  const promptDisabled = videoGenerationMutation.isPending && mode === 'prompt';
  const showManualProductPrompt = mode === 'custom' && mutation.isPending && !manualProductPromptDismissed && productName.trim() !== '';

  // 案件行は作らず、入力中の名前・説明・ブランド名を初期値にした新規登録ページ (登録時に作る) を別タブで開く
  const handleOpenManualProductForm = () => {
    const params = new URLSearchParams();
    params.set('title', productName.trim());
    if (sellingPoints.trim()) params.set('description', sellingPoints.trim());
    if (brandName.trim()) params.set('brand_name', brandName.trim());
    window.open(`/products/manual/new?${params.toString()}`, '_blank');
    setManualProductPromptDismissed(true);
  };

  // シナリオ生成完了・シナリオブック読み込み時に作業画面へ自動遷移
  useEffect(() => {
    if (scenarios.length > 0 && mode !== 'prompt') {
      changePhase('work');
    }
  }, [scenarios.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // 同じ商品でシナリオが存在する場合のみ「再生成」ラベルを表示
  const isRegenerate = useMemo(() => {
    if (!scenarios.length || !lastResult) return false;
    if (mode === 'asin') return asin.trim() === lastResult.asin;
    if (mode === 'manual') return manualProductId === lastResult.product_id;
    if (mode === 'custom') return productName.trim() === lastResult.custom_input?.product_name;
    return false;
  }, [scenarios.length, lastResult, mode, asin, manualProductId, productName]);

  const heroImageUrl = useMemo(() => selectedAssets[0]?.sourceUrl || null, [selectedAssets]);

  // 【暫定】選択中の素材（人物=AI俳優は selectedAssets に含まれないため自動的に除外される）を
  // 1枚のタイル画像にまとめてダウンロードする。
  const [isDownloadingAssetTile, setIsDownloadingAssetTile] = useState(false);
  const handleDownloadAssetTile = useCallback(async () => {
    if (selectedAssets.length === 0 || isDownloadingAssetTile) return;
    setIsDownloadingAssetTile(true);
    try {
      // 商品名・ブランド名は scenarios.custom_input を主ソースに（custom / manual モードで保存される）。
      // ASIN モードでは custom_input が無いため、商品名は productSummary、
      // ブランド名はシナリオに紐づく brands テーブル(activeScenarioBrand)にフォールバック。
      const ci = lastResult?.custom_input;
      const displayProductName = (ci?.product_name || productSummary || productName).trim();
      const displayBrandName = (ci?.brand_name || activeScenarioBrand.data?.brand_name || brandName).trim();
      // 動画生成に添付される統合画像と同じ絵をダウンロードできるよう、覆面化画像への
      // 差し替えも同条件で行う (buildStoryboardProductTile と揃える)。
      const { count, sanitizedCount } = await downloadSelectedAssetsTile(selectedAssets, {
        productName: displayProductName,
        brandName: displayBrandName,
        // フッターのラベルはブランド別用語（default「商品画像」/ hypeapp「プロジェクト画像」）
        footerLabel: `${brandConfig.labels.productShort}画像`,
      }, { preferFacePrivacySanitized: true });
      if (count > 0) {
        const sanitizedNote = sanitizedCount > 0 ? `（うち ${sanitizedCount} 枚は覆面化画像）` : '';
        toast.success(`選択中の素材 ${count} 枚をまとめてダウンロードしました${sanitizedNote}`);
      } else {
        toast.error('素材のダウンロードに失敗しました');
      }
    } catch {
      toast.error('素材のダウンロードに失敗しました');
    } finally {
      setIsDownloadingAssetTile(false);
    }
  }, [selectedAssets, isDownloadingAssetTile, toast, productName, productSummary, brandName, lastResult, activeScenarioBrand.data]);

  // 「商品画像を再生成」: 現在の selectedAssets から統合タイル画像を合成し直し、
  // 一時素材としてアップロードして保持する (state は useVideoHandlers より前で宣言)。
  // 以後のストーリーボード動画生成では商品メイン画像の代わりにこの画像が添付される
  // (storyboard_attached_image_url)。
  // 用途: 商品画像に顔が含まれ Seedance に弾かれた際、顔入り素材を選択から外して
  // 同じストーリーボードを別の商品画像で再生成するための回避手段。AI 生成では
  // ないためポイントは消費しない。
  const handleRegenerateProductTile = useCallback(async () => {
    if (selectedAssets.length === 0 || isRegeneratingProductTile) return;
    setIsRegeneratingProductTile(true);
    try {
      const next = await buildStoryboardProductTile();
      if (!next) {
        toast.error(`${brandConfig.labels.productShort}画像の再生成に失敗しました`);
        return;
      }
      const sanitizedNote = next.sanitizedCount > 0
        ? `（覆面化済みの素材 ${next.sanitizedCount} 枚は覆面化画像に差し替え）`
        : '';
      toast.success(`選択中の素材から${brandConfig.labels.productShort}画像を再生成しました${sanitizedNote}。次回の動画生成から使用されます`);
      // 実在人物フィルタが商品画像を指していた場合、再生成で作り直したので
      // 「商品画像を添付」チェックの赤枠 (原因ハイライト) を外す。他の原因素材
      // (ロゴ・絵コンテ・選択素材) のハイライトはそのまま残す。
      setFacePrivacyFlagged((prev) => {
        if (!prev) return prev;
        if (prev.scenarioId && prev.scenarioId !== activeScenarioId) return prev;
        const rest = prev.items.filter(
          (it) => it.kind !== 'product_image' && it.kind !== 'product_tile',
        );
        if (rest.length === prev.items.length) return prev;
        return rest.length > 0 ? { ...prev, items: rest } : null;
      });
    } catch {
      toast.error(`${brandConfig.labels.productShort}画像の再生成に失敗しました`);
    } finally {
      setIsRegeneratingProductTile(false);
    }
  }, [selectedAssets, isRegeneratingProductTile, toast, buildStoryboardProductTile, setFacePrivacyFlagged, activeScenarioId]);

  // シナリオ生成ボタンを非活性化する条件（入力未完 / ポイント不足 / ストレージ超過 / 送信中）
  const isScenarioSubmitDisabled = useMemo(() => {
    if (mode === 'prompt') return true;
    return mutation.isPending || !hasRequiredScenarioInput || isCreditInsufficient || scenarioCreditInsufficient || isStorageExceeded;
  }, [mode, mutation.isPending, hasRequiredScenarioInput, isCreditInsufficient, scenarioCreditInsufficient, isStorageExceeded]);

  // シナリオ生成ボタン下に表示する注意メッセージ
  const scenarioGuidanceMessages = useMemo(() => {
    const messages: Array<JSX.Element> = [];
    if ((mode === 'asin' && !asin.trim()) || (mode === 'manual' && !manualProductId)) {
      messages.push(<div key="input">商品を選択してください。</div>);
    } else if (mode === 'custom' && !productName.trim()) {
      messages.push(<div key="input">商品名を入力してください。</div>);
    }
    if (isCreditInsufficient || scenarioCreditInsufficient) {
      messages.push(
        <div key="credits">
          ポイントが不足しています。<button type="button" onClick={openPurchaseModal} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>ポイントを購入</button>してください。
        </div>
      );
    }
    if (isStorageExceeded) {
      messages.push(
        <div key="storage">
          ストレージ容量の上限に達しました。<a href="/trash">ごみ箱</a>を空にすると空き容量を確保できます。プランのアップグレードで容量を引き上げることも可能です。<a href="/subscription">プランを確認</a>
        </div>
      );
    }
    return messages;
  }, [mode, asin, manualProductId, productName, isCreditInsufficient, scenarioCreditInsufficient, isStorageExceeded, openPurchaseModal]);

  // Stickyボタン: 商品がセットされていて、送信ボタンが画面外の場合に表示
  const stickyEnabled = useMemo(() => {
    if (mode === 'prompt') return false;
    if (phase === 'work' && scenarios.length > 0) return false;
    return hasRequiredScenarioInput;
  }, [mode, phase, scenarios.length, hasRequiredScenarioInput]);

  const { submitRowRef, isSubmitOutOfView } = useStickySubmitVisibility(stickyEnabled);

  const showStickySubmit = stickyEnabled && isSubmitOutOfView;

  useLocalStorageSync({
    productName,
    brandName,
    autoGenerateFirstFrame,
    autoGenerateStoryboard,
    autoGenerateEndcardFrame,
    autoGenerateNarration,
    lastFrameMode,
    scenarioCount,
    scenarioGenerationModel,
    thinkingLevel,
    scenarioModel,
    tone,
    includeNarration,
    includeTelop,
    includeDialogue,
    excludePeople,
    playedVideos,
    playedNarrations,
  });

  useEffect(() => {
    if (scenarios.length) {
      if (!activeScenarioId && phase === 'work') {
        setActiveScenarioId(scenarios[0].id);
      }
    } else {
      setActiveScenarioId(null);
    }
  }, [scenarios, activeScenarioId, setActiveScenarioId, phase]);

  // 歌モノ (トーン option の metadata.bgm_mode==='lyrics') を選んだ瞬間だけ、画面の向きを
  // 縦 (9:16) に寄せてトーストで知らせる。setup フェーズ限定 (work フェーズは表示中シナリオの
  // aspect_ratio 同期が正)。既に 9:16 なら何もしない。歌モノ以外へ戻しても向きは戻さない
  // (ユーザーが選び直す前提。想定尺の自動引き上げと同じ扱い)。
  const prevLyricsToneRef = useRef(false);
  useEffect(() => {
    const wasLyrics = prevLyricsToneRef.current;
    prevLyricsToneRef.current = isLyricsTone;
    if (!isLyricsTone || wasLyrics) return;
    if (phase === 'work') return;
    if (videoSettings.aspectRatio === '9:16') return;
    setVideoSettings(prev => ({ ...prev, aspectRatio: '9:16' }));
    toast.show('歌モノのため、画面の向きを縦 (9:16) に変更しました');
  }, [isLyricsTone, phase, videoSettings.aspectRatio, setVideoSettings, toast]);

  // シナリオ切替時にアスペクト比と frame_mode を active シナリオに同期
  // (異なる frame_mode のシナリオが同じ画面に混在する場合に、タブ切替で UI を整合させる)
  //
  // 注意: ここでの videoGenerationMode 同期は「表示中シナリオに UI を合わせる」ためだけの
  // 一時変更で、localStorage には書かない (setVideoGenerationModeTransient)。永続セッターを
  // 使うと、たまたま開いた frame モードのシナリオによって「次に生成するときのデフォルト
  // モード」が storyboard → frame に silently 上書きされ、次バッチが意図せず first_only で
  // 生成される (実際に起きた)。生成デフォルトは setup フェーズ進入時の復元側で管理する。
  // work フェーズ (既存シナリオの閲覧/編集) 限定。
  useEffect(() => {
    if (phase !== 'work') return;
    if (!activeScenarioId || !scenarios.length) return;
    const scenario = scenarios.find(s => s.id === activeScenarioId);
    if (!scenario) return;
    // aspect_ratioが有効な値の場合のみ同期（未設定の場合は現在のvideoSettingsを維持）
    const ar = scenario.aspect_ratio;
    if (ar === '16:9' || ar === '9:16') {
      setVideoSettings(prev => prev.aspectRatio !== ar ? { ...prev, aspectRatio: ar } : prev);
    }
    // videoGenerationMode 同期 (非永続)。正本は generation_metadata の
    // production_mode/per_scene_default_mode (無ければレガシー
    // video_generation_mode/frame_mode。P2 step 7-6・§9 分類A)。frame_mode 単独だと、
    // ボード未生成で frame_mode が storyboard へ未昇格の storyboard シナリオを frame と
    // 誤判定するため、helper 経由で gm を優先する
    // (docs/requirements/storyboard-mode-loading-fix-plan.md)。
    const nextMode = deriveLoadedVideoGenerationMode(scenario);
    if (videoGenerationMode !== nextMode) {
      setVideoGenerationModeTransient(nextMode);
    }
    // 推奨ナレーター (性別 / 年代) を generation_metadata から復元し、声フィルターの既定値に使う。
    // 保存済みシナリオを開き直したとき用 (生成直後は成功ハンドラが同じ値を set 済み)。
    // キーが無いシナリオ (旧データ) は現在値を維持する。
    const rn = (scenario.generation_metadata as { recommended_narrator?: { gender?: string; age?: string } | null } | undefined)?.recommended_narrator;
    if (rn?.gender) {
      setRecommendedNarrator(prev => (prev?.gender === rn.gender && prev?.age === rn.age ? prev : { gender: rn.gender, age: rn.age }));
    }
  }, [activeScenarioId, scenarios, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ストーリーボード生成トグル (顔画像を含めない / ロゴ添付 / 商品画像添付) を
  // シナリオ単位で DB (generation_metadata) に保存・復元する。
  //  - シナリオ切替時: 保存値があれば UI に復元 (無ければ localStorage の現在値が既定)
  //  - 同一シナリオ内で値が変わった時: 差分のみ PUT (楽観的・fire-and-forget)
  // 生成 payload には引き続き現在の UI 値を載せるため (=正本)、PUT 遅延/失敗で
  // 生成結果が食い違うことはない。
  const scenarioToggleSyncRef = useRef<{
    scenarioId: string | null;
    avoidFaces: boolean;
    illustration: boolean;
    logo: boolean;
    product: boolean;
  } | null>(null);
  useEffect(() => {
    if (phase !== 'work' || !activeScenarioId || !scenarios.length) return;
    const scenario = scenarios.find((s) => s.id === activeScenarioId);
    if (!scenario) return;
    const meta = (scenario.generation_metadata ?? {}) as Record<string, unknown>;
    const prev = scenarioToggleSyncRef.current;

    if (!prev || prev.scenarioId !== activeScenarioId) {
      // シナリオ切替/初回マウント: DB のスナップショットを UI に復元する。
      // キーが無いシナリオ (旧データ/未保存) は現在値 (localStorage 既定) を維持。
      const nextAvoid = typeof meta.avoid_faces === 'boolean' ? meta.avoid_faces : avoidFaces;
      const nextIllustration = typeof meta.illustration_storyboard === 'boolean' ? meta.illustration_storyboard : illustrationStoryboard;
      const nextLogo = typeof meta.include_storyboard_logo === 'boolean' ? meta.include_storyboard_logo : includeStoryboardLogo;
      const nextProduct = typeof meta.include_storyboard_product_image === 'boolean' ? meta.include_storyboard_product_image : includeStoryboardProductImage;
      if (nextAvoid !== avoidFaces) setAvoidFaces(nextAvoid);
      if (nextIllustration !== illustrationStoryboard) setIllustrationStoryboard(nextIllustration);
      if (nextLogo !== includeStoryboardLogo) setIncludeStoryboardLogo(nextLogo);
      if (nextProduct !== includeStoryboardProductImage) setIncludeStoryboardProductImage(nextProduct);
      scenarioToggleSyncRef.current = { scenarioId: activeScenarioId, avoidFaces: nextAvoid, illustration: nextIllustration, logo: nextLogo, product: nextProduct };
      return;
    }

    // 同一シナリオ内でユーザーがトグルした → 差分を generation_metadata にマージ保存。
    const partial: UpdateScenarioPayload = {};
    if (prev.avoidFaces !== avoidFaces) partial.avoid_faces = avoidFaces;
    if (prev.illustration !== illustrationStoryboard) partial.illustration_storyboard = illustrationStoryboard;
    if (prev.logo !== includeStoryboardLogo) partial.include_storyboard_logo = includeStoryboardLogo;
    if (prev.product !== includeStoryboardProductImage) partial.include_storyboard_product_image = includeStoryboardProductImage;
    scenarioToggleSyncRef.current = { scenarioId: activeScenarioId, avoidFaces, illustration: illustrationStoryboard, logo: includeStoryboardLogo, product: includeStoryboardProductImage };
    if (Object.keys(partial).length === 0) return;
    updateScenario(activeScenarioId, partial).catch((err) => {
      console.warn('Failed to persist scenario generation toggles:', err);
    });
  }, [activeScenarioId, scenarios, phase, avoidFaces, illustrationStoryboard, includeStoryboardLogo, includeStoryboardProductImage, setAvoidFaces, setIllustrationStoryboard, setIncludeStoryboardLogo, setIncludeStoryboardProductImage]);

  // ナレ/セリフ/テロップ復元(フェーズ6)。属性ツリー取得完了(scenarioAttributesLoaded)を待ってから
  // 1シナリオにつき1回だけ実行する。これにより直リンク等で「シナリオ先行→ツリー後着」のときも
  // tree option ロード後に確実に *_mode→key 復元できる(切替時のみの復元だと取りこぼす race を回避)。
  // tree 有効: 保存済み *_mode を option key へ逆引き(会話/後付けも正確に)。tree 無効(legacy):
  // include_* / telop_generate_only から legacy 値へ。該当キーが無い旧シナリオは現在値を維持。
  const behaviorRestoredScenarioIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (phase !== 'work' || !activeScenarioId || !scenarios.length) return;
    if (!scenarioAttributesLoaded) return; // ツリー取得完了まで待つ(treeEnabled/除外が確定する)
    if (behaviorRestoredScenarioIdRef.current === activeScenarioId) return; // 1シナリオ1回
    const scenario = scenarios.find((s) => s.id === activeScenarioId);
    if (!scenario) return;
    const meta = (scenario.generation_metadata ?? {}) as Record<string, unknown>;
    const restoreBehavior = (
      categoryEnabled: boolean,
      treeEnabled: boolean,
      treeOpts: { value: string; mode: string | null }[],
      modeVal: unknown,
      legacyValue: () => string | undefined,
      setter: React.Dispatch<React.SetStateAction<string>>,
    ) => {
      if (categoryEnabled) {
        // フラグ ON。tree 有効(非除外)で *_mode 保存済みなら key へ逆引き。除外/未保存は現在値維持。
        if (treeEnabled && typeof modeVal === 'string') {
          const opt = treeOpts.find((o) => o.mode === modeVal);
          setter(opt ? opt.value : 'unspecified');
        }
        return;
      }
      // フラグ OFF(legacy)。include_* / telop_generate_only から復元。キーが無ければ現在値維持。
      const v = legacyValue();
      if (v !== undefined) setter(v);
    };
    restoreBehavior(narrationCategoryEnabled, narrationTreeEnabled, narrationTreeOptions, meta.narration_mode,
      () => (typeof meta.include_narration === 'boolean' ? (meta.include_narration ? 'include' : 'exclude') : undefined),
      setIncludeNarration);
    restoreBehavior(dialogueCategoryEnabled, dialogueTreeEnabled, dialogueTreeOptions, meta.dialogue_mode,
      () => (typeof meta.include_dialogue === 'boolean' ? (meta.include_dialogue ? 'include' : 'exclude') : undefined),
      setIncludeDialogue);
    restoreBehavior(telopCategoryEnabled, telopTreeEnabled, telopTreeOptions, meta.telop_mode,
      () => (meta.telop_generate_only === true ? 'generate_only'
        : typeof meta.include_telop === 'boolean' ? (meta.include_telop ? 'include' : 'exclude') : undefined),
      setIncludeTelop);
    // 登場人物数の復元: option key(character_count_key)優先、無ければ整数(character_count)から
    // 'cc_<n>' を組み立て。tree に存在する key のときのみ反映する。
    // ユーザー操作後にこの effect が遅延実行されると、保存値なしの旧シナリオで '' に戻り
    // cap=1 になって2人目選択が単数置換されるため、ユーザー編集済みなら上書きしない。
    const characterCountEditedForActiveScenario = characterCountUserEditedScenarioRef.current === activeScenarioId;
    if (characterCountTreeEnabled && !characterCountEditedForActiveScenario) {
      // 復元される実俳優数。cap(登場人物数)はこれを下回ってはならない。下回ると trim effect が
      // 復元済みの追加俳優を解除し、sticky が1人に削られて「1人にした」トーストが出る。
      // generation_metadata 依存は再取得/RPCマージで欠ける場合があるため、top-level の
      // character_count 列と実俳優数もフォールバックに使う (不変条件: cap >= 選択俳優数)。
      const sc = scenario as {
        character_count?: number;
        casting_models?: unknown[]; casting_model_ids?: unknown[];
        seedance_preset_actors?: unknown[]; seedance_preset_actor_ids?: unknown[];
      };
      const restoredActorCount = Math.max(
        Array.isArray(sc.casting_models) ? sc.casting_models.length
          : (Array.isArray(sc.casting_model_ids) ? sc.casting_model_ids.length : 0),
        Array.isArray(sc.seedance_preset_actors) ? sc.seedance_preset_actors.length
          : (Array.isArray(sc.seedance_preset_actor_ids) ? sc.seedance_preset_actor_ids.length : 0),
      );
      // 保存された人数(metaN)。key→整数、無ければ meta/列の整数値。情報なしは 0。
      const parseN = (k: unknown): number => {
        const m = typeof k === 'string' ? k.match(/\d+/) : null;
        return m ? parseInt(m[0], 10) : 0;
      };
      const metaN = typeof meta.character_count_key === 'string' ? parseN(meta.character_count_key)
        : typeof meta.character_count === 'number' ? meta.character_count
        : typeof sc.character_count === 'number' ? sc.character_count
        : 0;
      // 不変条件: cap(登場人物数) は復元される実俳優数を下回ってはならない。保存値が実俳優数より
      // 小さくても (旧バグで1人保存・未設定→1解決 等) floor を優先し、復元済み俳優を trim しない。
      const effectiveN = Math.max(metaN, restoredActorCount);
      if (effectiveN >= 2) {
        const key = `cc_${effectiveN}`;
        if (characterCountTreeOptions.some((o) => o.value === key)) {
          setCharacterCountState(key);
        }
        // option に該当キーが無い場合は現在値を維持 (誤って '' に戻して trim しない)。
      } else if (metaN >= 1) {
        const key = `cc_${metaN}`;
        setCharacterCountState(characterCountTreeOptions.some((o) => o.value === key) ? key : '');
      } else {
        // 情報なし & 単数/0人 → 従来どおり未指定。
        setCharacterCountState('');
      }
    }
    if (characterCountUserEditedScenarioRef.current === activeScenarioId) {
      characterCountUserEditedScenarioRef.current = null;
    }
    behaviorRestoredScenarioIdRef.current = activeScenarioId;
  }, [activeScenarioId, scenarios, phase, scenarioAttributesLoaded,
    narrationCategoryEnabled, narrationTreeEnabled, narrationTreeOptions,
    dialogueCategoryEnabled, dialogueTreeEnabled, dialogueTreeOptions,
    telopCategoryEnabled, telopTreeEnabled, telopTreeOptions,
    characterCountTreeEnabled, characterCountTreeOptions,
    setIncludeNarration, setIncludeDialogue, setIncludeTelop,
    setCharacterCountState, characterCountUserEditedScenarioRef]);

  // 新規生成 (setup フェーズ) に戻ったら、閲覧中に一時変更された videoGenerationMode を
  // ユーザーが明示選択したモード (localStorage) へ復元する。これにより新規バッチの生成は
  // 常に「ユーザーが選んだモード」で行われ、直前に閲覧したシナリオの frame_mode に
  // 引きずられない。prompt mode は別キー管理のため対象外。
  useEffect(() => {
    if (mode === 'prompt') return;
    if (phase === 'setup') {
      restoreVideoGenerationMode();
    }
  }, [phase, mode, restoreVideoGenerationMode]);

  // 最初のフレームの画像を切り替えたときに前シーンの最後のフレームを連動更新する
  // (確定された素材を selectedAssets に追加する自動同期は廃止済み。§9 step-1 項目8)
  useFirstFrameSync({
    scenarios,
    scenePreviewIndex,
    scenePreviewMaterials,
    sceneLastFrames,
    setSceneLastFrames,
    updateSceneMutation,
  });

  const { productImageFetchError, setProductImageFetchError, productImageFetching } = useProductMaterials({
    enabled: phase === 'setup',
    asin,
    manualProductId,
    fetchedAsins,
    setFetchedAsins,
    fetchedProductIds,
    setFetchedProductIds,
    addAsset,
    requestOpenPersonalFolder,
    toast,
    queryClient,
  });

  // 動画素材をドロップしてシーンに紐づけるハンドラ
  const handleDropVideoMaterial = async (scenario: Scenario, scene: Scenario['scenes'][number], materialData: DroppedMaterial) => {
    const key = `${scenario.id}-scene-${scene.order}`;

    // ドロップされた素材のIDを取得
    const materialId = materialData.id;
    if (!materialId) {
      toast.error('素材のIDが見つかりません');
      return;
    }

    // 動画素材かどうかを確認
    const contentType = materialData.contentType || materialData.content_type || '';
    if (!contentType.startsWith('video/')) {
      toast.error('動画素材のみドロップできます');
      return;
    }

    // 動画の実尺を取得 (素材に duration が無くてもクライアント側で確定できる)。
    // ブランドカット(エンドカード等)では実尺がそのままクリップ長になるため、ドロップ
    // 直後からタイムライン表示・エクスポート尺を正しくするために保存しておく。
    // 取得失敗時は尺なしで紐付けのみ行う (再読込時に backend が素材側から補完する)。
    const probedDuration = await probeVideoDurationSeconds(
      materialData.sourceUrl || materialData.source_url,
    );

    // バックエンドに保存
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: {
          video_material_id: materialId,
          ...(probedDuration ? { video_duration_seconds: Math.round(probedDuration) } : {}),
        },
      });

      // ローカルステートを更新
      setSceneVideoMaterialId((prev) => ({ ...prev, [key]: materialId }));
      setSceneVideoGenerated((prev) => ({ ...prev, [key]: true }));

      toast.success('動画素材を紐づけました');
    } catch (error) {
      console.error('動画素材の紐づけに失敗:', error);
      toast.error('動画素材の紐づけに失敗しました');
    }
  };

  const scenarioSettingsSummary = useMemo(() => {
    const parts: string[] = [];
    // 台本取り込み中は先頭に台本のファイル名。トーン・ナレ/セリフ/テロップ・方言は台本に従うため要約に出さない
    if (importedScript) parts.push(`台本: ${importedScript.filename}`);
    if (sourceVideo) parts.push(sourceVideoSummaryLabel(sourceVideo));
    parts.push(`${effectiveScenarioCount}案`);
    if (duration) {
      // ツリー有効時 duration は option key。選択肢からラベルを引く（旧値/Express の素数値は
      // フォールバックで "N秒" 整形、最後は素のまま）。
      const durLabel = durationSelectOptions.find((o) => o.value === duration)?.label;
      if (durLabel) {
        parts.push(durLabel);
      } else {
        const secMatch = duration.match(/(\d+)/);
        parts.push(secMatch ? `${secMatch[1]}秒` : duration);
      }
    }
    // ナレ/セリフ/テロップの要約。tree 有効時は option key→label を引く(会話/後付け等)。
    // 無効時は legacy 値で従来表記。'unspecified' は無表示。
    const behaviorSummary = (
      catLabel: string,
      value: string,
      treeEnabled: boolean,
      treeOptions: { value: string; label: string; mode?: string | null }[],
      legacy: Record<string, string>,
    ): string | null => {
      if (!value || value === 'unspecified') return null;
      if (treeEnabled) {
        const opt = treeOptions.find((o) => o.value === value);
        if (!opt) return null;
        // 会話モード(「あり（会話）」等)はサマリでは「会話」だけを表示する
        if (opt.mode === 'conversation') return '会話';
        // 後付けモード(「あり（後付け）」等)は「テロップ後付」のように簡略表示する
        if (opt.mode === 'generate_only') return `${catLabel}後付`;
        return `${catLabel}${opt.label}`;
      }
      return legacy[value] ?? null;
    };
    const narrPart = importedScript ? null : behaviorSummary('ナレーション', includeNarration, narrationTreeEnabled, narrationTreeOptions,
      { include: 'ナレーションあり', exclude: 'ナレーションなし' });
    if (narrPart) parts.push(narrPart);
    const dlgPart = importedScript ? null : behaviorSummary('セリフ', includeDialogue, dialogueTreeEnabled, dialogueTreeOptions,
      { include: 'セリフあり', exclude: 'セリフなし' });
    if (dlgPart) parts.push(dlgPart);
    const telopPart = importedScript ? null : behaviorSummary('テロップ', includeTelop, telopTreeEnabled, telopTreeOptions,
      { include: 'テロップあり', generate_only: 'テロップ後付', exclude: 'テロップなし' });
    if (telopPart) parts.push(telopPart);
    // 「モーショングラフィックス」(§10.14)。全編のときは映像スタイル欄を隠しているので要約にも出さない
    const motionGraphicsValue = effectiveMotionGraphics(motionGraphics, motionGraphicsOptions);
    const motionGraphicsLabel = motionGraphicsOptions.find((o) => o.key === motionGraphicsValue)?.label;
    if (motionGraphicsLabel) parts.push(motionGraphicsLabel);
    if (visualStyle && visualStyle !== 'none' && motionGraphicsValue !== MOTION_GRAPHICS_WHOLE) {
      // 映像スタイルは option key。階層オプションから再帰でラベルを引く。
      const findVisualStyleLabel = (opts: HierarchicalOption[]): string | undefined => {
        for (const o of opts) {
          if (o.type === 'option' && o.key === visualStyle) return o.label;
          if (o.children) { const f = findVisualStyleLabel(o.children); if (f) return f; }
        }
        return undefined;
      };
      const visualStyleLabel = findVisualStyleLabel(visualStyleHierarchicalOptions);
      if (visualStyleLabel) parts.push(visualStyleLabel);
    }
    const shootingLabel = findShootingMethodLabel(shootingMethodOptions, shootingMethod);
    if (shootingLabel) parts.push(shootingLabel);
    const dialectLabel = importedScript ? undefined : findShootingMethodLabel(dialectOptions, dialect);
    if (dialectLabel) parts.push(dialectLabel);
    // ターゲット性別・年代 (「20代女性」)。ツリーに無い値は出さない
    const targetLabel = formatTargetAudienceSummary(targetGenderOptions, targetGender, targetAgeOptions, targetAge);
    if (targetLabel) parts.push(targetLabel);
    if (tone && !importedScript) {
      // tree モードでは tone は option key。階層オプションから再帰でラベルを引く
      // (旧モードでも key=value なので一致する)。フォールバックで旧 options も参照。
      const findToneLabel = (opts: HierarchicalOption[]): string | undefined => {
        for (const o of opts) {
          if (o.type === 'option' && o.key === tone) return o.label;
          if (o.children) { const f = findToneLabel(o.children); if (f) return f; }
        }
        return undefined;
      };
      const toneLabel = findToneLabel(toneHierarchicalOptions)
        ?? promptOptions?.tone?.options.find((o) => o.value === tone)?.label;
      if (toneLabel) parts.push(toneLabel);
    }
    if (scenarioModel) {
      // ティア表記はカタログ (scenarioModelCatalog) から導出し、二重管理を避ける
      parts.push(
        scenarioModel === SCENARIO_MODEL_RANDOM ? 'ランダム' : scenarioModelTierLabel(scenarioModel)
      );
    }
    if (additionalPrompt.trim()) {
      parts.push('追加プロンプトあり');
    }
    return parts;
  }, [importedScript, sourceVideo, effectiveScenarioCount, tone, duration, visualStyle, visualStyleHierarchicalOptions, motionGraphics, motionGraphicsOptions, shootingMethod, shootingMethodOptions, dialect, dialectOptions, targetGender, targetGenderOptions, targetAge, targetAgeOptions, includeNarration, includeDialogue, includeTelop, narrationTreeEnabled, narrationTreeOptions, dialogueTreeEnabled, dialogueTreeOptions, telopTreeEnabled, telopTreeOptions, promptOptions, toneHierarchicalOptions, durationSelectOptions, scenarioModel, additionalPrompt]);

  // 動画モデルの短縮表示ラベル (例: "Veo高品質" / "SD2.5標準")。
  // display_label が "Seedance 2.0" のようにプロバイダ+バージョン形式の場合は、
  // 品質トークンが欠落しているとみなして "標準" を補う。
  // Seedance は 2.0/2.5 系が同名 (標準) で判別できないため、metadata.ui_series_label
  // (世代 '2.5'/'2.0') を含めた "SD2.5標準" 形式にする (2026-08-18 ユーザー指示)。
  const shortVideoModelLabel = useMemo(() => {
    const PROVIDER_SHORT: Record<string, string> = { veo: 'Veo', seedance: 'Seedance', kling: 'Kling', minimax: 'MiniMax' };
    const model = videoGenerationModels.find((m) => m.model_code === videoSettings.videoModel);
    if (!model) return videoSettings.videoModel;
    const providerKey = model.provider ?? '';
    const providerShort = PROVIDER_SHORT[providerKey]
      || (providerKey ? providerKey.charAt(0).toUpperCase() + providerKey.slice(1) : '');
    let qualityLabel = (model.display_label || '').trim();
    if (/[\d.]/.test(qualityLabel)) {
      qualityLabel = '標準';
    }
    const seriesLabel = ((model.metadata as { ui_series_label?: string } | null | undefined)?.ui_series_label || '').trim();
    if (providerKey === 'seedance' && seriesLabel) {
      return `SD${seriesLabel}${qualityLabel}`;
    }
    return `${providerShort}${qualityLabel}`;
  }, [videoGenerationModels, videoSettings.videoModel]);

  // シナリオモード用の動画生成設定サマリ（音声有無は含めない）
  const videoSettingsSummaryScenario = useMemo(() => {
    const parts: string[] = [];
    // 全編モーションは動画モデルを使わない (設定欄でも選べない) ので、サマリにも出さない
    if (!isMotionEngine) parts.push(shortVideoModelLabel);
    parts.push(formatVideoResolutionLabel(videoSettings.resolution));
    parts.push(videoSettings.aspectRatio);
    // リサイズモードは既定 (crop) のときサマリに出さない。pad を選んだときだけ表示。
    if (videoSettings.resizeMode === 'pad') parts.push('パディング');
    if (autoGenerateNarration) parts.push('ナレーション自動生成');
    return parts.join(' / ');
  }, [shortVideoModelLabel, videoSettings, autoGenerateNarration, isMotionEngine]);

  // プロンプト入力モード用の動画生成設定サマリ（動画の長さを含める。音声は常時オンなので出さない）
  const videoSettingsSummaryPrompt = useMemo(() => {
    const parts: string[] = [];
    parts.push(shortVideoModelLabel);
    parts.push(videoSettings.duration === -1 ? '自動' : `${videoSettings.duration}秒`);
    parts.push(formatVideoResolutionLabel(videoSettings.resolution));
    parts.push(videoSettings.aspectRatio === 'adaptive' ? '自動' : videoSettings.aspectRatio);
    // リサイズモードは既定 (crop) のときサマリに出さない。pad を選んだときだけ表示。
    if (videoSettings.resizeMode === 'pad') parts.push('パディング');
    return parts.join(' / ');
  }, [shortVideoModelLabel, videoSettings]);

  // isReferenceImageMode は上方で定義済み（推定クレジット計算と共有）

  // SetupPanel 配下へ供給するシナリオ設定フォームの Context 値。
  // props バケツリレー (旧 155 個) の代わりに、フォームの値・setter・選択肢・派生表示を
  // ここでまとめて渡す。親のオーケストレーション系は従来どおり SetupPanel の props。
  // memo 化しない: 従来の props 渡しと同じ再描画特性を保つため。
  const setupContextValue: ScenarioSetupContextValue = {
    ...form,
    importedScript,
    setImportedScript,
    sourceVideo,
    setSourceVideo,
    mode,
    setMode,
    scenarioInputTab,
    setScenarioInputTab,
    videoGenerationMode,
    setVideoGenerationMode,
    isReferenceImageMode,
    avoidFaces,
    setAvoidFaces,
    illustrationStoryboard,
    setIllustrationStoryboard,
    visualStyle,
    setVisualStyle,
    visualStyleHierarchicalOptions,
    visualStyleTreeEnabled,
    visualStyleIsStylized,
    visualStyleFieldPlacement,
    onVisualStyleChange: handleVisualStyleChange,
    toneHierarchicalOptions: toneOptionsForSetup,
    onToneChange: handleToneChange,
    onDurationChange: handleDurationChange,
    isLyricsTone,
    isMotionEngine,
    toneRecommendedKeys,
    visualStyleRecommendedKeys,
    motionGraphicsRecommendedKeys,
    recommendedDuration,
    characterCountTreeEnabled,
    narrationTreeOptions,
    narrationTreeEnabled,
    narrationCategoryEnabled,
    dialogueTreeOptions,
    dialogueTreeEnabled,
    dialogueCategoryEnabled,
    telopTreeOptions,
    telopTreeEnabled,
    telopCategoryEnabled,
    scenarioSettingsSummary,
    videoSettingsSummaryScenario,
    videoSettingsSummaryPrompt,
    scenarioModelOptions,
    scenarioModelDefs,
    randomRateBadge,
    currentScenarioModelDef,
    selectedScenarioModelRate,
    randomCreditText,
    scenarioCreditCost,
    estimatedVideoCredits,
    aggregatesReferenceVideoSeconds,
    referenceVideoSecondsTotal,
    hasCeiledReferenceVideoDuration,
    imageGenerationModels,
    castingModelsQuery,
    DEFAULT_SCENARIO_MODEL,
    GENERATION_MODEL_SPEED,
    videoSettings,
    setVideoSettings,
    autoGenerateNarration,
    setAutoGenerateNarration,
    selectedCasting,
    setSelectedCasting,
    additionalCastings,
    setAdditionalCastings,
    castingSelectionState,
    setCastingSelectionState,
    markCastingExplicit,
    selectedSeedancePresetActor,
    setSelectedSeedancePresetActor,
    additionalSeedancePresetActors,
    setAdditionalSeedancePresetActors,
  };

  // SceneItem 配下 (カルーセル/フレームタブ/詳細列/フローアクション) が参照する
  // シーン操作ハンドラ + 共通参照データ。SceneHandlersProvider で供給し、
  // WorkPanel 経由のバケツリレー (旧 sceneItemBase) は行わない。
  const sceneHandlersValue: SceneHandlersContextValue = {
    expandedConfirmButton,
    expandedNarrations,
    expandedVideos,
    dragOverLastFrameKey,
    dragOverSceneKey,
    isGlobalDragging,
    isDraggingVideo,
    pendingContinuityScenes,
    setPendingContinuityScenes,
    editingField,
    editingValue,
    deletingScenes,
    addingScenes,
    swappingScenes,
    videoSettings,
    videoGenerationMode,
    avoidFaces,
    // 素材 @メンション候補 (docs/features/material-at-mention-plan.md レビュー指摘 P1)。
    // SceneDetailColumn のシーン説明欄ポップアップが使う (プロンプトモード基準の近似)。
    mentionEligibleAssets,
    // AI俳優 @メンション候補 (同「俳優メンション」節)。casting/seedancePreset は排他で
    // 常に候補になる (参照上限フィルタ対象外)。
    mentionActorSources,
    // 動画生成(reference-to-video)の参照にキャラクターシートを含めるトグル。
    // 各シーンの動画カードに表示する (キャスティング選択 かつ 参照/ストーリーボードモード)。
    // トグルはキャラクター俳優の CS には適用しない (計画書 修正3-b) ため、フルキャスト
    // (主 + 追加俳優) に人間俳優が1人もいない場合は死にスイッチになるトグル自体を隠す。
    includeCastingSheet: videoSettings.includeCastingSheet !== false,
    showCastingSheetToggle: !!selectedCasting
      && (videoGenerationMode === 'reference' || videoGenerationMode === 'storyboard')
      && shouldShowCastingSheetToggle([...(selectedCasting ? [selectedCasting] : []), ...additionalCastings]),
    onIncludeCastingSheetChange: (next: boolean) => setVideoSettings((prev) => ({ ...prev, includeCastingSheet: next })),
    manualProductId,
    heroImageUrl,
    asin,
    creditBadgeText,
    isCreditInsufficient,
    isStorageExceeded,
    creditRatesData,
    creditsData,
    narrationBlockedByVocalLeadBgm: bgm.bgmIsVocalLead,
    narrationEnglishScenes,
    setNarrationEnglishScenes,
    selectedNarrationProvider,
    setSelectedNarrationProvider,
    restoreNarrationProvider,
    recommendedNarrator,
    narratorAccent,
    voicesData,
    voicesLoading,
    voiceNameMap,
    tempMaterials,
    cloudMaterials,
    downloadingMaterials,
    setExpandedNarrations,
    setExpandedVideos,
    setDragOverLastFrameKey,
    setDragOverSceneKey,
    setEditingField,
    setEditingValue,
    setPreviewModalMaterial,
    setPreviewModalSceneId,
    setLastResult,
    lastResult,
    handleGenerateScenePreview,
    handleConfirmFirstFrame,
    handleClearFirstFrame,
    handleDropMaterial,
    handleDropLastFrameMaterial,
    handleRemoveLastFrame,
    handleRestoreLastFrame,
    handleGenerateLastFrame,
    handleGenerateSceneVideo,
    handleGenerateSwipeClip,
    handleGenerateStoryboardMotionOverlay,
    handleGenerateWholeMotion,
    handleReviseWholeMotion,
    wholeMotionRunning,
    wholeMotionStage,
    handleGenerateVoiceLines,
    handleRetakeVoiceLine,
    refreshVoiceScenario,
    voiceLinesRunning,
    voiceLinesLastResult,
    handleSelectVideo,
    handleToggleVideoExcluded,
    handleToggleNarrationExcluded,
    handleSelectNarration,
    handleSetAudioTrackType,
    handleSelectSoundLogo,
    handleMergeComplete,
    handleSaveField,
    handleSwapSceneOrder,
    handleAddSceneBelow: (scenarioId: string, afterSceneId: string) => handleAddScene(scenarioId, { afterSceneId }),
    handleDeleteScene,
    handleDownloadMaterial,
    handleDropVideoMaterial,
    getSceneVideoSettings,
    updateSceneVideoSettings,
    applyScenarioVideoSettingsToScenes,
    getSceneStoryboardDuration,
    updateSceneStoryboardDuration,
    applySceneStoryboardDurations,
    updateSceneBrandCutDuration,
    getPreviewWidth,
    openFrameDropdown,
    openVideoModelDropdown,
    openMaterialsPanel,
    openPurchaseModal,
    updateSceneMutation,
    sceneFrameSettings,
    collectSceneFrameReferenceCandidates,
    ensureAddedMaterialsResolved,
    forgetAddedMaterial,
    deleteSceneMutation,
    createSceneMutation,
    videoDropZoneRefs,
    videoPlayButtonRefs,
    editingDialogueLinesRef,
    editingTelopLinesRef,
    fetchMaterialById,
    motionSyncStatus,
  };

  // 各シナリオで生成ジョブ（フレーム・ラストフレーム・動画・ナレーション・一括生成・
  // ストーリーボード）が走っているかを集約。タブの生成中インジケータ（虹バー）と
  // 完了ドットの点灯判定に使う。scene 系のローディングは sceneKey
  // (`${scenario.id}-scene-${order}`) で引けるので prefix 一致で拾う。
  const scenarioGeneratingMap = useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    const hasSceneJob = (m: Record<string, boolean>, prefix: string) =>
      Object.keys(m).some((k) => k.startsWith(prefix) && m[k]);
    // 利用者の操作待ちで止まっているシェル (全編モーションの BGM 確認待ち): 虹色の生成中表示を出さない
    const pausedIds = new Set(activeJobs.filter((j) => j.paused && j.scenarioId).map((j) => j.scenarioId as string));
    for (const sc of scenarios) {
      const prefix = `${sc.id}-scene-`;
      if (pausedIds.has(sc.id)) { map[sc.id] = false; continue; }
      map[sc.id] =
        sc.status === 'generating' ||  // ランダム生成のスケルトンシェル (本文待ち)
        !!batchGeneratingFrames[sc.id] ||
        !!bgm.bgmGeneratingScenarios[sc.id] ||
        hasSceneJob(scenePreviewLoading, prefix) ||
        hasSceneJob(lastFrameLoading, prefix) ||
        hasSceneJob(sceneVideoLoading, prefix) ||
        activeJobs.some((j) => j.status === 'polling' && j.scenarioId === sc.id);
    }
    return map;
  }, [
    scenarios,
    batchGeneratingFrames,
    bgm.bgmGeneratingScenarios,
    scenePreviewLoading,
    lastFrameLoading,
    sceneVideoLoading,
    activeJobs,
  ]);

  return (
    <div className={styles.container}>
      {/* モード切替タブ（常に表示、折りたたみの外） */}
      <div className="gen-mode-section" style={{ justifyContent: 'flex-start' }}>
        <div className="gen-mode-switch">
          {brandConfig.id === 'hypeapp' ? (
            <>
              <button
                type="button"
                className={`gen-mode-button ${mode === 'prompt' ? 'gen-mode-button-active' : ''}`}
                onClick={() => {
                  setMode('prompt');
                  onModeChange?.('prompt');
                  if (phase === 'work') {
                    changePhase('setup');
                    setActiveScenarioId(null);
                  }
                }}
                disabled={mutation.isPending || videoGenerationMutation.isPending}
              >
                プロンプト入力<span className="gen-mode-button-suffix">モード</span>
              </button>
              <button
                type="button"
                className={`gen-mode-button ${mode !== 'prompt' ? 'gen-mode-button-active' : ''}`}
                onClick={() => {
                  if (scenarioInputTab === 'product') {
                    setMode(manualProductId ? 'manual' : 'asin');
                  } else {
                    setMode('custom');
                  }
                  onModeChange?.('attributes');
                }}
                disabled={mutation.isPending || videoGenerationMutation.isPending}
              >
                {brandConfig.labels.scenarioModeBase}<span className="gen-mode-button-suffix">モード</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className={`gen-mode-button ${mode !== 'prompt' ? 'gen-mode-button-active' : ''}`}
                onClick={() => {
                  if (scenarioInputTab === 'product') {
                    setMode(manualProductId ? 'manual' : 'asin');
                  } else {
                    setMode('custom');
                  }
                  onModeChange?.('attributes');
                }}
                disabled={mutation.isPending || videoGenerationMutation.isPending}
              >
                {brandConfig.labels.scenarioModeBase}<span className="gen-mode-button-suffix">モード</span>
              </button>
              <button
                type="button"
                className={`gen-mode-button ${mode === 'prompt' ? 'gen-mode-button-active' : ''}`}
                onClick={() => {
                  setMode('prompt');
                  onModeChange?.('prompt');
                  if (phase === 'work') {
                    changePhase('setup');
                    setActiveScenarioId(null);
                  }
                }}
                disabled={mutation.isPending || videoGenerationMutation.isPending}
              >
                プロンプト入力<span className="gen-mode-button-suffix">モード</span>
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          className={styles.expressButton}
          onClick={() => window.dispatchEvent(new Event('openExpressMode'))}
        >
          <span className={styles.expressIcon}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </span>
          <span className={styles.expressLabel}>特急<span className={styles.expressLabelSuffix}>モード</span></span>
        </button>
      </div>

      {/* ウィザードコンテナ: 条件付きレンダリングで設定画面/作業画面を切替 */}
      <div className={styles.wizardContainer}>

        {/* ===== Setup Panel: 設定画面 ===== */}
        {phase === 'setup' && (
          <ScenarioSetupProvider value={setupContextValue}>
          <SetupPanel
            scenarios={scenarios}
            changePhase={changePhase}
            hasTransitioned={hasTransitioned}
            product={productSelection}
            accordions={accordions}
            frameDropdown={frameDropdown}
            mutation={mutation}
            videoGenerationMutation={videoGenerationMutation}
            handleSubmit={handleSubmit}
            productSelectRef={productSelectRef}
            showProductTooltip={showProductTooltip}
            setShowProductTooltip={setShowProductTooltip}
            setIsScenarioBookOpen={setIsScenarioBookOpen}
            setIsProductSelectorOpen={setIsProductSelectorOpen}
            productImageFetchError={productImageFetchError}
            setProductImageFetchError={setProductImageFetchError}
            productImageFetching={productImageFetching}
            clearAssets={clearAssets}
            selectedAssets={selectedAssets}
            mentionEligibleAssets={mentionEligibleAssets}
            mentionActorSources={mentionActorSources}
            asinDisabled={asinDisabled}
            customDisabled={customDisabled}
            promptDisabled={promptDisabled}
            handleVideoGenerationModeChange={handleVideoGenerationModeChange}
            promptTextareaRef={promptTextareaRef}
            isVideoCreditInsufficient={isVideoCreditInsufficient}
            handleGenerateVideoFromPrompt={handleGenerateVideoFromPrompt}
            motionLayeredPrompt={motionLayeredPrompt}
            promptVideoGenerated={promptVideoGenerated}
            promptVideoMaterialId={promptVideoMaterialId}
            promptVideoDisplayRef={promptVideoDisplayRef}
            tempMaterials={tempMaterials}
            cloudMaterials={cloudMaterials}
            setPreviewModalMaterial={setPreviewModalMaterial}
            handleVideoHistoryPreview={handleVideoHistoryPreview}
            handleRestoreVideoSettings={handleRestoreVideoSettings}
            openPurchaseModal={openPurchaseModal}
            resolveErrorMessage={resolveErrorMessage}
            showVideoPromptHistory={showVideoPromptHistory}
            setShowVideoPromptHistory={setShowVideoPromptHistory}
            handleSelectVideoPromptHistory={handleSelectVideoPromptHistory}
            isScenarioSubmitDisabled={isScenarioSubmitDisabled}
            isRegenerate={isRegenerate}
            scenarioGuidanceMessages={scenarioGuidanceMessages}
            submitRowRef={submitRowRef}
            isStorageExceeded={isStorageExceeded}
            showManualProductPrompt={showManualProductPrompt}
            handleOpenManualProductForm={handleOpenManualProductForm}
          />
          </ScenarioSetupProvider>
        )}{/* /setupPanel */}

        {/* ===== Work Panel: 作業画面 ===== */}
        {phase === 'work' && (
          <SceneHandlersProvider value={sceneHandlersValue}>
          <WorkPanel
            scenarios={scenarios}
            activeScenarioId={activeScenarioId}
            mode={mode}
            changePhase={changePhase}
            setActiveScenarioId={setActiveScenarioId}
            scenarioHeadingRef={scenarioHeadingRef}
            handleClearScenario={handleClearScenario}
            setIsScenarioBookOpen={setIsScenarioBookOpen}
            tabsDrag={tabsDrag}
            handleTabClick={handleTabClick}
            handleCloseScenarioTab={handleCloseScenarioTab}
            scenarioGeneratingMap={scenarioGeneratingMap}
            scenarioInfoModal={scenarioInfoModal}
            videoSettings={videoSettings}
            videoGenerationMode={toScenarioVideoGenerationMode(videoGenerationMode)}
            batchGeneratingFrames={batchGeneratingFrames}
            setShowBatchCancelModal={setShowBatchCancelModal}
            handleBatchGenerateFirstFrames={handleBatchGenerateFirstFrames}
            handleToggleStar={handleToggleStar}
            handleTogglePublish={handleTogglePublish}
            isDownloading={isDownloading}
            handleDownloadScenario={handleDownloadScenario}
            setShowClearAllFramesConfirm={setShowClearAllFramesConfirm}
            handleCopyScenarioJson={handleCopyScenarioJson}
            handleDeleteScenario={handleDeleteScenario}
            handleDuplicateScenario={handleDuplicateScenario}
            showClearAllFramesConfirm={showClearAllFramesConfirm}
            handleClearAllFrames={handleClearAllFrames}
            showBatchCancelModal={showBatchCancelModal}
            batchFrameCancelRefs={batchFrameCancelRefs}
            videoGenerationModels={videoGenerationModels}
            lastResult={lastResult}
            setLastResult={setLastResult}
            setVideoSettings={setVideoSettings}
            updateScenarioMutation={updateScenarioMutation}
            currentUserId={user?.user_uid}
            setAddSceneModalScenarioId={setAddSceneModalScenarioId}
            createSceneMutation={createSceneMutation}
            bgm={bgm}
            creditRatesData={creditRatesData}
            getSceneVideoSettings={getSceneVideoSettings}
            handleRemoveVideoCard={handleRemoveVideoCard}
            handleDownloadMaterial={handleDownloadMaterial}
            castingGenderFilter={castingGenderFilter}
            castingAgeFilter={castingAgeFilter}
            handleDownloadAssetTile={handleDownloadAssetTile}
            isDownloadingAssetTile={isDownloadingAssetTile}
            handleRegenerateProductTile={handleRegenerateProductTile}
            isRegeneratingProductTile={isRegeneratingProductTile}
            pendingSceneSkeletons={pendingSceneSkeletons}
            onMotionShellReady={(id) => { void handleLoadScenario(id, { focus: id === activeScenarioId }); }}
          />
          </SceneHandlersProvider>
        )}{/* /workPanel */}

      </div>

      {/* 全編モーション (§10.5): 生成ボタン直後に BGM を先に決めるモーダル */}
      <MotionBgmChoiceModal
        isOpen={!!motionBgmRequest}
        scenarioCredits={motionScenarioCredits}
        expectedSeconds={motionExpectedSeconds}
        creditRates={creditRatesData?.rates}
        onCancel={() => setMotionBgmRequest(null)}
        onConfirm={(choice) => {
          const req = motionBgmRequest;
          setMotionBgmRequest(null);
          req?.submit(choice);
        }}
      />

      {/* Scenario Book Modal */}
      <ScenarioBookModal
        isOpen={isScenarioBookOpen}
        onClose={() => setIsScenarioBookOpen(false)}
        onSelectScenario={handleLoadScenario}
        onSelectScenarios={handleLoadScenarios}
        currentUserId={user?.user_uid}
        loadedScenarioIds={scenarios.map(s => s.id)}
      />

      {/* 公開確認モーダル (ワークスペースの公開導線。採用素材の共有移行を選ぶ) */}
      {publishConfirmDialog}

      {/* シナリオ読み込み中のローディング表示 (ビューポート基準) */}
      {isLoadingScenario && <LoadingOverlay message="シナリオを読み込み中..." fullScreen />}

      {/* シーン追加モーダル */}
      {addSceneModalScenarioId && (() => {
      // ブランド新規作成直後で、未リンクの商品があるときだけ関連付けプロンプトが出る。
      // その間はモーダルを開いたままにする (= 即閉じ + スケルトンを抑止)。
      const linkPromptPending = brandJustCreatedInModal
        && productBrandInfo.data?.product_type != null
        && !productBrandInfo.data?.current_brand_id
        && linkProductStatus !== 'success';
      return (
        <AddSceneModal
          isOpen={!!addSceneModalScenarioId}
          onClose={() => {
            if (!aiContinueMutation.isPending && !productLastCutMutation.isPending && !logoLastCutMutation.isPending && !titleCardMutation.isPending) {
              setAddSceneModalScenarioId(null);
              setLinkProductStatus('idle');
            }
          }}
          onSelectManual={() => {
            handleAddScene(addSceneModalScenarioId);
            setAddSceneModalScenarioId(null);
          }}
          onSelectAiContinue={() => {
            handleAiContinueScene(addSceneModalScenarioId);
            // 全ルートで即閉じ + スケルトン表示。ただしブランド新規作成直後の
            // リンクプロンプト中だけはモーダルを開いたままにする。
            if (!linkPromptPending) setAddSceneModalScenarioId(null);
          }}
          onSelectProductCut={() => {
            handleProductLastCut(addSceneModalScenarioId);
            if (!linkPromptPending) setAddSceneModalScenarioId(null);
          }}
          onSelectLogoCut={() => {
            const scenarioId = addSceneModalScenarioId;
            handleLogoLastCut(scenarioId);
            if (!linkPromptPending) setAddSceneModalScenarioId(null);
          }}
          onSelectTitleCard={() => {
            const scenarioId = addSceneModalScenarioId;
            handleTitleCard(scenarioId);
            if (!linkPromptPending) setAddSceneModalScenarioId(null);
          }}
          onAttachFromMaterial={(position, imageUrl, materialId) => {
            handleBrandCutFromMaterial(addSceneModalScenarioId, { position, kind: 'logo', imageUrl, materialId });
            setAddSceneModalScenarioId(null);
          }}
          hasTitleCard={(() => {
            const sc = scenarios.find(s => s.id === addSceneModalScenarioId);
            return !!sc?.scenes?.some(s => s.brand_cut_position === 'start');
          })()}
          hasEndcard={(() => {
            const sc = scenarios.find(s => s.id === addSceneModalScenarioId);
            return !!sc?.scenes?.some(s => s.brand_cut_position === 'end');
          })()}
          hasLogo={brandLogoStatus.data?.has_logo ?? false}
          hasBrand={!!brandLogoStatus.data?.brand_id}
          brandId={brandLogoStatus.data?.brand_id || null}
          isGenerating={aiContinueMutation.isPending || productLastCutMutation.isPending || logoLastCutMutation.isPending || titleCardMutation.isPending}
          generatingLabel={addSceneGeneratingLabel}
          scenarioId={addSceneModalScenarioId}
          brandName={brandLogoStatus.data?.brand_name}
          productBrandInfo={productBrandInfo.data}
          onLinkProduct={() => {
            if (!productBrandInfo.data || !brandLogoStatus.data?.brand_id) return;
            setLinkProductStatus('pending');
            linkProductMutation.mutate(
              {
                brandId: brandLogoStatus.data.brand_id,
                productType: productBrandInfo.data.product_type as 'amazon' | 'manual',
                productId: productBrandInfo.data.product_id!,
              },
              {
                onSuccess: () => setLinkProductStatus('success'),
                onError: () => setLinkProductStatus('idle'),
              }
            );
          }}
          onSkipLink={() => setLinkProductStatus('success')}
          linkProductStatus={linkProductStatus}
          onBrandJustCreated={() => {
            setBrandJustCreatedInModal(true);
            brandLogoStatus.refetch();
            productBrandInfo.refetch();
          }}
          onBrandLinked={async (selectedBrandId, linkProduct) => {
            if (linkProduct && productBrandInfo.data?.product_id) {
              await linkProductMutation.mutateAsync({
                brandId: selectedBrandId,
                productType: productBrandInfo.data.product_type as 'amazon' | 'manual',
                productId: productBrandInfo.data.product_id,
              });
            }
            await linkScenarioBrandMutation.mutateAsync({
              scenarioId: addSceneModalScenarioId!,
              brandId: selectedBrandId,
            });
            brandLogoStatus.refetch();
            productBrandInfo.refetch();
          }}
          autoGenerateFrame={autoGenerateEndcardFrame}
          onAutoGenerateFrameChange={setAutoGenerateEndcardFrame}
          aspectRatio={videoSettings.aspectRatio}
          isStoryboard={scenarios.find(s => s.id === addSceneModalScenarioId)?.frame_mode === 'storyboard'}
        />
      );
      })()}

      {mutation.isError ? <div className={styles.error}>シナリオ生成中にエラーが発生しました。</div> : null}

      {/* 商品選択モーダル */}
      <ProductSelectorModal
        isOpen={isProductSelectorOpen}
        onClose={() => setIsProductSelectorOpen(false)}
        onSelectLinks={(links) => {
          if (links.length > 0) {
            const link = links[0];
            if (link.product_type === 'amazon' && link.asin) {
              // Amazon商品: ASINを設定
              // 商品が変更された場合は選択済み素材をクリア
              if (link.asin !== asin) {
                clearAssets();
                // 素材をクリアしたら取得済みフラグも消し、選択した ASIN の自動補完が
                // 再実行されるようにする (残すと scope が空のまま生成され商品が消える)。
                const nextAsin = link.asin;
                setFetchedAsins((prev) => {
                  const next = new Set(prev);
                  next.delete(nextAsin);
                  return next;
                });
              }
              setAsin(link.asin);
              // マニュアル商品の状態をクリア（相互排他）
              setManualProductId(null);
              setManualProductTitle(null);
              setManualProductImageUrl(null);
              setMode('asin');
            } else if (link.product_type === 'manual') {
              // マニュアル登録商品: product_idを設定してmanualモードに切り替え
              // 商品が変更された場合は選択済み素材をクリア
              if (link.product_id !== manualProductId) {
                clearAssets();
                // 素材をクリアしたら取得済みフラグも消し、選択した商品の自動補完が
                // 再実行されるようにする。
                const nextProductId = link.product_id;
                setFetchedProductIds((prev) => {
                  const next = new Set(prev);
                  next.delete(nextProductId);
                  return next;
                });
              }
              setManualProductId(link.product_id);
              setManualProductTitle(link.title || null);
              setManualProductImageUrl(link.main_image_url || null);
              // ASINの状態をクリア（相互排他）
              setAsin('');
              setMode('manual');
            }
          }
          setIsProductSelectorOpen(false);
        }}
        initialSelectedAsins={(() => {
          // selectedAssetsからASINを抽出してブランド自動選択に利用
          const asinsFromAssets = selectedAssets
            .map(asset => {
              if (typeof asset.asin === 'string') return asset.asin;
              if (Array.isArray(asset.asin) && asset.asin.length > 0) return asset.asin[0];
              return null;
            })
            .filter((asin): asin is string => asin !== null);

          // 現在選択中のasinも含める
          if (asin && !asinsFromAssets.includes(asin)) {
            return [asin, ...asinsFromAssets];
          }
          return asinsFromAssets.length > 0 ? asinsFromAssets : (asin ? [asin] : []);
        })()}
        maxSelection={1}
      />

      {/* 素材プレビューモーダル */}
      {previewModalMaterial && (
        <MaterialPreviewModal
          material={previewModalMaterial}
          onClose={() => { setPreviewModalMaterial(null); setPreviewModalSceneId(null); }}
          onNavigate={async (direction) => {
            const currentScenario = scenarios.find(s => s.id === (activeScenarioId ?? scenarios[0]?.id));
            if (!currentScenario) return;
            const scenesWithImage = currentScenario.scenes.filter(s => s.image_material_id);
            const currentIndex = scenesWithImage.findIndex(s => s.id === previewModalSceneId);
            if (currentIndex === -1) return;
            const nextIndex = currentIndex + direction;
            if (nextIndex < 0 || nextIndex >= scenesWithImage.length) return;
            const nextScene = scenesWithImage[nextIndex];
            if (nextScene.image_material_id) {
              const fullMaterial = await fetchMaterialById(nextScene.image_material_id);
              if (fullMaterial) {
                setPreviewModalMaterial(fullMaterial);
                setPreviewModalSceneId(nextScene.id);
              }
            }
          }}
          showEditControls={true}
          isTemporary={true}
        />
      )}

      {/* 動画ドロップオーバーレイ（ポータル） - 視覚的な表示のみ、ドロップはフレームエリアで処理 */}
      {isGlobalDragging && isDraggingVideo && createPortal(
        <>
          {Array.from(videoPlayButtonRefs.current.entries()).map(([refKey, el]) => {
            const rect = el.getBoundingClientRect();
            return (
              <div
                key={refKey}
                style={{
                  position: 'fixed',
                  top: rect.top + rect.height / 2,
                  left: rect.left + rect.width / 2,
                  transform: 'translate(-50%, -50%)',
                  width: '112px',
                  height: '63px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed rgba(255, 255, 255, 0.6)',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(60, 60, 60, 0.95)',
                  pointerEvents: 'auto',
                  zIndex: Z_INDEX.dropdown,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  cursor: 'copy',
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  
                  // refKeyから該当するシナリオとシーンを特定
                  let targetScenario: Scenario | undefined;
                  let targetScene: Scenario['scenes'][number] | undefined;
                  
                  for (const s of scenarios) {
                    const found = s.scenes.find(sc => `${s.id}-scene-${sc.order}` === refKey);
                    if (found) {
                      targetScenario = s;
                      targetScene = found;
                      break;
                    }
                  }

                  if (!targetScenario || !targetScene) return;

                  try {
                    const dataString = e.dataTransfer.getData('application/json');
                    if (!dataString) return;

                    const materialData = JSON.parse(dataString);

                    // 動画素材の場合はhandleDropVideoMaterialを呼ぶ
                    if (materialData.contentType && materialData.contentType.startsWith('video/')) {
                      handleDropVideoMaterial(targetScenario, targetScene, materialData);
                    } else {
                      toast.error('動画素材のみドロップできます');
                    }
                  } catch (error) {
                    console.error('ドロップ処理エラー:', error);
                  }
                }}
              >
                <span
                  style={{
                    fontSize: '11px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    textAlign: 'center',
                    lineHeight: '1.4',
                    fontWeight: 500,
                  }}
                >
                  動画を<br />ドロップ
                </span>
              </div>
            );
          })}
        </>,
        document.body
      )}

      {/* Stickyフローティング生成ボタン */}
      {showStickySubmit && (
        <div className={styles.stickySubmitContainer}>
          <GenerateButton
            label={isRegenerate ? brandConfig.labels.scenarioRegenerate : brandConfig.labels.scenarioGenerate}
            creditCost={scenarioCreditCost}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingLabel={`${brandConfig.labels.scenarioGenerate}中…`}
            disabled={isScenarioSubmitDisabled}
          />
        </div>
      )}

      {/* 確認モーダル群 (モデル切替ガード / 動画生成の直前確認)。描画順は従来どおり。 */}
      <ScenarioDialogs
        dialogs={dialogs}
        aspectMismatchConfirmOpen={aspectMismatchConfirmOpen}
        onAspectMismatchConfirm={handleAspectMismatchConfirm}
        onAspectMismatchChangeAndConfirm={handleAspectMismatchChangeAndConfirm}
        onAspectMismatchCancel={handleAspectMismatchCancel}
        noFrameConfirmOpen={noFrameConfirmOpen}
        noFrameConfirmDontShow={noFrameConfirmDontShow}
        setNoFrameConfirmDontShow={setNoFrameConfirmDontShow}
        onNoFrameConfirm={handleNoFrameConfirm}
        onNoFrameCancel={handleNoFrameCancel}
        frameMismatchConfirm={frameMismatchConfirm}
        onFrameMismatchSelect={handleFrameMismatchSelect}
        onFrameMismatchConfirm={handleFrameMismatchConfirm}
        onFrameMismatchCancel={() => setFrameMismatchConfirm(null)}
      />

      {purchaseModals}

      {/* フレーム生成モデル選択ドロップダウン（Portal） */}
      <FrameModelDropdownPortal
        dropdown={frameDropdown}
        scenarioGenerationModel={scenarioGenerationModel}
        setScenarioGenerationModel={setScenarioGenerationModel}
        imageGenerationModels={imageGenerationModels}
        creditRatesData={creditRatesData}
        videoSettings={videoSettings}
      />

      {/* 動画モデル選択ドロップダウン（Portal） */}
      <VideoModelDropdownPortal
        dropdown={videoModelDropdown}
        sceneKey={videoModelDropdownSceneKey}
        videoGenerationModels={videoGenerationModels}
        videoSettings={videoSettings}
        setVideoSettings={setVideoSettings}
        videoGenerationMode={videoGenerationMode}
        getSceneVideoSettings={getSceneVideoSettings}
        updateSceneVideoSettings={updateSceneVideoSettings}
        creditRatesData={creditRatesData}
      />
    </div>
  );
}

export default ScenarioGenerator;
